import React, { useState, useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
// import SettingsIcon from '@mui/icons-material/Settings';
// import EditSurfaceIcon from '@mui/icons-material/BorderColor';
import EditSurfaceIcon from '@mui/icons-material/ModeEdit';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ReplyIcon from '@mui/icons-material/Reply';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import SwitchRightIcon from '@mui/icons-material/SwitchRight';
// import ImageIcon from '@mui/icons-material/Image';
import DeveloperBoardOffIcon from '@mui/icons-material/DeveloperBoardOff';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import EngravingIcon from '@mui/icons-material/FormatShapes';
import StarIcon from '@mui/icons-material/Star';
import './PropertyPanel.css';
import { useDesignStore } from '../../context/DesignContext';
import { useViewerStore, ViewerTabsMode } from 'context/ViewerContext';
import { useFootMeshStore } from 'context/FootMeshContext';
import { Hardness, MeasurementSystem } from '../Viewer3d/consts';
import { ProfileCurveTemplates, ConditionSide, InsoleType, ShoeSize, Insole, resolveInsoleTemplateApiName, resolveProfileCurveName, profileCurveSelectKey } from 'models/Insole';
import { DesignCategory } from 'models/InsoleDesign';
import { addNewAddition } from '../../services/designService'
import { useApi } from "../../context/ApiProvider";
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { isExhibitionNarrationDesign } from 'Exhibition/exhibitionCapabilities';
import { InsoleHighLevelParams } from '../../models/InsoleHighLevelParams';
import { Addition, AdditionNewStatus, AutomaticAdditions } from '../../models/Addition';
import Engraving from 'components/EngravingPanel';
import { undoLastAutosave } from "components/Viewer3d/UndoStack";
import { AutoProperties } from 'models/AutoProperties';
import { mirrorToLeft, mirrorToRight } from 'components/Viewer3d/Mirror';
import { DEFAULT_HARDNESS, GCodeSetting } from 'models/GcodeSetting';
import { useAuth } from 'context/AuthContext';
import { canEditDesign, canUseDevelopmentUi } from 'auth/permissions';
import { User } from 'models/User';
import { Box, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NumberSpinnerNoKeyboard from 'components/Spinners/NumberSpinnerNoKeyboard';
import CustomMUISelect from 'components/Form/CustomMUISelect';
import { isDesktop, isMobile, isTablet } from 'Utils/MediaQueryUtils';
import { convertMmToViewerUnit, convertUnitToDefault, LengthUnit } from 'Utils/MeasurementUtils';
import { BandJoints, outsoleThicknessJoint } from 'data/highLevelJointsSandal';
import { HighHeelHeight } from 'data/highHeelJoints';

interface PropertyPanelProps {
  activeTab?: ViewerTabsMode | undefined;
  openRequestId?: number;
}

export type additionNameGroup = {
  group: string;
  fileNames: string[];
};

const PropertyPanel: React.FC<PropertyPanelProps> = ({ activeTab, openRequestId }) => {
  const designLoaded = useDesignStore((state) => state.designLoaded);
  const insoleDesign = useDesignStore((state) => state.insoleDesign);
  // {/*Property Panel: Project Setting*/}
  const pressureTransparency = useViewerStore(state => state.pressureTransparency);
  const setPressureTransparency = useViewerStore(state => state.setPressureTrancparency);
  const liftedInsoleTransparency = useViewerStore(state => state.liftedInsoleTransparency);
  const setLiftedInsoleTransparency = useViewerStore(state => state.setLiftedInsoleTransparency);
  const shoeDataTransparency = useViewerStore(state => state.shoeDataTransparency);
  const setShoeDataTransparency = useViewerStore(state => state.setShoeDataTransparency);
  const bonesTransparency = useViewerStore(state => state.bonesTransparency);
  const setBonesTransparency = useViewerStore(state => state.setBonesTransparency);

  const insolesVisible = useViewerStore(state => state.insolesVisible);
  const toggleInsolesVisible = useViewerStore(state => state.toggleInsolesVisible);
  const bandHeightClipping = useViewerStore(state => state.bandHeightClipping);
  const toggleBandHeightClipping = useViewerStore(state => state.toggleBandHeightClipping);

  const gcodesVisible = useViewerStore(state => state.gcodeVisible);
  const toggleGCodesVisible = useViewerStore(state => state.toggleGCodeVisible);

  const topInsoleDefaultColor = useViewerStore(state => state.topInsoleDefaultColor);
  const setTopInsoleDefaultColor = useViewerStore(state => state.setTopInsoleDeafultColor);

  const bottomInsoleDefaultColor = useViewerStore(state => state.bottomInsoleDefaultColor);
  const setBottomInsoleDefaultColor = useViewerStore(state => state.setBottomInsoleDeafultColor);

  const liftedInsoleDefaultColor = useViewerStore(state => state.liftedInsoleDefaultColor);
  const setLiftedInsoleDefaultColor = useViewerStore(state => state.setLiftedInsoleDefaultColor);

  const shoeDataDefaultColor = useViewerStore(state => state.shoeDataDefaultColor);
  const setShoeDataDefaultColor = useViewerStore(state => state.setShoeDataDefaultColor);

  const coloring = useViewerStore(state => state.coloring);

  const additionsViewMeshVisible = useViewerStore(state => state.additionsViewMeshVisible);
  const toggleAdditionsViewMeshVisible = useViewerStore(state => state.toggleAdditionsViewMeshVisible);
  const liftedInsoleMeshVisible = useViewerStore(state => state.liftedInsoleMeshVisible);
  const toggleLiftedInsoleMeshVisible = useViewerStore(state => state.toggleLiftedInsoleMeshVisible);
  const bonesMeshVisible = useViewerStore(state => state.bonesMeshVisible);
  const toggleBonesMeshVisible = useViewerStore(state => state.toggleBonesMeshVisible);
  const footMeshVisible = useViewerStore(state => state.footMeshVisible);
  const toggleFootMeshVisible = useViewerStore(state => state.toggleFootMeshVisible);
  const hasFootMesh = useFootMeshStore(state => state.rightFoot !== null || state.leftFoot !== null);
  const shoeDataMeshVisible = useViewerStore(state => state.shoeDataMeshVisible);
  const toggleShoeDataMeshVisible = useViewerStore(state => state.toggleShoeDataMeshVisible);
  const highHeelMeshVisible = useViewerStore(state => state.highHeelMeshVisible);
  const toggleHighHeelMeshVisible = useViewerStore(state => state.toggleHighHeelMeshVisible);
  const animatePressure = useViewerStore(state => state.animatePressure);
  const toggleAnimatePressure = useViewerStore(state => state.toggleAnimatePressure);

  const gcodeStatus = useViewerStore((state) => state.gcodeStatus);
  const finalize = useDesignStore((state) => state.GcodeReady);

  const showVertexMarkerPosition = useViewerStore((state) => state.showMarkerPosition);

  const [internalActiveTab, setInternalActiveTab] = useState<ViewerTabsMode>(ViewerTabsMode.View);
  const [advancedIsActive, setAdvancedIsActive] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState<boolean>(false);
  const [additionImagesFilename, setAdditionImagesFilename] = useState<additionNameGroup[]>([]);
  const [selectedAdditionImage, setSelectedAdditionImage] = useState<string>('');
  const api = useApi();
  const [propertyValues, setPropertyValues] = useState<InsoleHighLevelParams[]>([]);

  const contentRef = useRef<HTMLDivElement>(null);

  const [isAutomaticOffloadAdditionsEnable, setIsAutomaticOffloadAdditionsEnable] = useState<boolean>(false);
  const [isLimbLengthEnable, setIsLimbLengthEnable] = useState<boolean>(false);

  const [additionHeights, setAdditionHeights] = useState<Record<string, string>>({});
  const [isVisible, setVisible] = useState<boolean>(() => isDesktop());
  const [isRightHeelMedialWedgeEditEnabled, setRightHeelMedialWedgeEditEnabled] = useState<boolean>(true);
  const [isLeftHeelMedialWedgeEditEnabled, setLeftHeelMedialWedgeEditEnabled] = useState<boolean>(true);
  const [isRightForeFootMedialWedgeEditEnabled, setRightForeFootMedialWedgeEditEnabled] = useState<boolean>(true);
  const [isLeftForeFootMedialWedgeEditEnabled, setLeftForeFootMedialWedgeEditEnabled] = useState<boolean>(true);
  const [isRightHeelLateralWedgeEditEnabled, setRightHeelLateralWedgeEditEnabled] = useState<boolean>(true);
  const [isLeftHeelLateralWedgeEditEnabled, setLeftHeelLateralWedgeEditEnabled] = useState<boolean>(true);
  const [isRightForeFootLateralWedgeEditEnabled, setRightForeFootLateralWedgeEditEnabled] = useState<boolean>(true);
  const [isLeftForeFootLateralWedgeEditEnabled, setLeftForeFootLateralWedgeEditEnabled] = useState<boolean>(true);
  const isSandal = useDesignStore((state) => state.insoleDesign?.isSandal);
  const designCategory = useDesignStore((state) => state.insoleDesign?.category);

  const rightMetatarsalgia = useRef<HTMLInputElement>(null);
  const leftMetatarsalgia = useRef<HTMLInputElement>(null);

  const rightHalluxLimitus = useRef<HTMLInputElement>(null);
  const leftHalluxLimitus = useRef<HTMLInputElement>(null);

  const rightSesamoiditis = useRef<HTMLInputElement>(null);
  const leftSesamoiditis = useRef<HTMLInputElement>(null);

  const leftMetatarsalBarInput = useRef<HTMLInputElement>(null);
  const rightMetatarsalBarInput = useRef<HTMLInputElement>(null);
  const leftMetatarsalPadInput = useRef<HTMLInputElement>(null);
  const rightMetatarsalPadInput = useRef<HTMLInputElement>(null);
  const leftMortonsExtensionInput = useRef<HTMLInputElement>(null);
  const rightMortonsExtensionInput = useRef<HTMLInputElement>(null);
  const leftDancerPadInput = useRef<HTMLInputElement>(null);
  const rightDancerPadInput = useRef<HTMLInputElement>(null);
  const selectedAdditionIndex = useViewerStore(state => state.selectedAdditionFromViewer.additionIndex);
  const selectedFoot = useViewerStore(state => state.selectedAdditionFromViewer.foot);
  const voiceAutomaticRef = useRef<(detail: any) => Promise<void>>(async () => { });
  const leftCutoutMet1 = useRef<HTMLInputElement>(null);
  const rightCutoutMet1 = useRef<HTMLInputElement>(null);
  const leftCutoutMet2 = useRef<HTMLInputElement>(null);
  const rightCutoutMet2 = useRef<HTMLInputElement>(null);
  const leftCutoutMet3 = useRef<HTMLInputElement>(null);
  const rightCutoutMet3 = useRef<HTMLInputElement>(null);
  const leftCutoutMet4 = useRef<HTMLInputElement>(null);
  const rightCutoutMet4 = useRef<HTMLInputElement>(null);
  const leftCutoutMet5 = useRef<HTMLInputElement>(null);
  const rightCutoutMet5 = useRef<HTMLInputElement>(null);
  const [limbLengthAmount, setLimbLengthAmount] = useState<number>(10);
  const [limbLengthFoot, setLimbLengthFoot] = useState<string>('Right');
  const [isPropertyPanelTabsEnabled, setPropertyPanelTabsEnabled] = useState<boolean>(true);
  const autoParams = useRef<AutoProperties>(new AutoProperties());
  const autoParamsInit = useRef<boolean>(false);
  // to implement onchange event 
  const additionHeightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoDesignTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRefs = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  const [leftForefootHardness, setLeftForefootHardness] = useState<Hardness | null>(DEFAULT_HARDNESS);
  const [rightForefootHardness, setRightForefootHardness] = useState<Hardness | null>(DEFAULT_HARDNESS);
  const [leftMidInnerHardness, setLeftMidInnerHardness] = useState<Hardness | null>(DEFAULT_HARDNESS);
  const [rightMidInnerHardness, setRightMidInnerHardness] = useState<Hardness | null>(DEFAULT_HARDNESS);
  const [leftMidOuterHardness, setLeftMidOuterHardness] = useState<Hardness | null>(DEFAULT_HARDNESS);
  const [rightMidOuterHardness, setRightMidOuterHardness] = useState<Hardness | null>(DEFAULT_HARDNESS);
  const [leftHeelHardness, setLeftHeelHardness] = useState<Hardness | null>(DEFAULT_HARDNESS);
  const [rightHeelHardness, setRightHeelHardness] = useState<Hardness | null>(DEFAULT_HARDNESS);
  const [size, setSize] = useState<ShoeSize>(ShoeSize.Size35);
  const [isDynamicPressure, setIsDynamicPressure] = useState<boolean>(false);
  const [propertyStep, setPropertyStep] = useState<number>(1);
  const [thicknessPropertyStep, setThicknessPropertyStep] = useState<number>(0.5);
  const [metricChanged, setMetricChanged] = useState<boolean>(false);
  const auth = useAuth();
  let authenticatedUser = auth.user;
  const canUseDevTools = canUseDevelopmentUi(authenticatedUser, auth.isExhibition);
  const measurementSystem: MeasurementSystem = useViewerStore(state => state.measurementSystem);
  const [hasBones, setHasBones] = useState<boolean>(false);
  const thirdQuarter = useDesignStore((state) => state.thirdQuarter);

  useEffect(() => {
    const handleResize = () => {
      setVisible(isDesktop());
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const defaultLengthUnits: Record<MeasurementSystem, LengthUnit> = {
    [MeasurementSystem.metric]: 'mm',
    [MeasurementSystem.imperial]: 'inch',
  };

  const getDefaultLengthUnit = () => {
    return defaultLengthUnits[measurementSystem];
  }

  useEffect(() => {
    if (!authenticatedUser) {
      authenticatedUser = JSON.parse(localStorage.getItem('user') || '{}') as User;
    }
    // Listen for updates from ThreeViewer
    const handleSurfaceJointUpdate = (event: CustomEvent) => {
      setTimeout(() => {
        validatePropertyValues(event.detail);
        setPropertyValues([...event.detail]);
        setSize(Insole.GetShoeSize([...event.detail][0]?.length));
      }, 0);
    };

    document.addEventListener('surfaceJointUpdate', handleSurfaceJointUpdate as EventListener);
    document.addEventListener('newWBCTScan', newWBCT as unknown as EventListener);

    return () => {
      document.removeEventListener('surfaceJointUpdate', handleSurfaceJointUpdate as EventListener);
      document.removeEventListener('newWBCTScan', newWBCT as unknown as EventListener);
    };
  }, []);

  useEffect(() => {
    const handleAutomaticDesignConditionUpdate = (event: CustomEvent) => {
      autoParams.current = event.detail;
      updateAutomaticParameters();
    };

    const refreshAutoParamsComponents = (event: CustomEvent) => {
      updateAutomaticParameters();
    }

    //'automaticDesignParametersUpdate' calls in Viewer3D
    //'refreshAutomaticComponents' calls in UndoStack
    document.addEventListener('automaticDesignParametersUpdate', handleAutomaticDesignConditionUpdate as EventListener);
    document.addEventListener('refreshAutomaticComponents', refreshAutoParamsComponents as EventListener);

    return () => {
      document.removeEventListener('automaticDesignParametersUpdate', handleAutomaticDesignConditionUpdate as EventListener);
      document.removeEventListener('refreshAutomaticComponents', refreshAutoParamsComponents as EventListener);
    };
  }, []);

  useEffect(() => {
    if (activeTab === undefined) {
      setIsContentVisible(false);
      return;
    }

    setInternalActiveTab(activeTab);
    setIsContentVisible(true);
    useViewerStore.getState().setMode(activeTab);
  }, [activeTab, openRequestId]);

  useEffect(() => {
    if (internalActiveTab === ViewerTabsMode.View && isContentVisible) {
      updateAutomaticParameters();
    }
  }, [internalActiveTab, isContentVisible]);

  const validatePropertyValues = (propertyValueParams: InsoleHighLevelParams[]) => {
    if (!propertyValueParams || propertyValueParams.length < 2) return;

    const validateWedge = (wedgeType: string, setMedial: (v: boolean) => void, setLateral: (v: boolean) => void) => {
      setMedial(wedgeType !== 'Lateral');
      setLateral(wedgeType !== 'Medial');
    };

    validateWedge(propertyValueParams[0].forefootWedgeType, setRightForeFootMedialWedgeEditEnabled, setRightForeFootLateralWedgeEditEnabled);
    validateWedge(propertyValueParams[1].forefootWedgeType, setLeftForeFootMedialWedgeEditEnabled, setLeftForeFootLateralWedgeEditEnabled);
    validateWedge(propertyValueParams[0].heelWedgeType, setRightHeelMedialWedgeEditEnabled, setRightHeelLateralWedgeEditEnabled);
    validateWedge(propertyValueParams[1].heelWedgeType, setLeftHeelMedialWedgeEditEnabled, setLeftHeelLateralWedgeEditEnabled);
  };

  const isPropertyValuesValidFn = (values: InsoleHighLevelParams[]): boolean => {
    if (values.length === 0) return false;

    const res = values.every(
      item => item !== undefined && item !== null
    );
    return res;
  };

  const isPropertyValuesValid = useMemo(() => isPropertyValuesValidFn(propertyValues), [propertyValues]);

  useEffect(() => {
    if (isPropertyValuesValid == false) return;
    const size = Insole.GetShoeSize(propertyValues[0]?.length)
    setSize(size);
    // console.log(propertyValues[0]);
  }, [isPropertyValuesValid])

  useEffect(() => {
    if (isAutomaticOffloadAdditionsEnable) {
      updateCutoutAdditions();
    }
    else {
      let allCutoutAddition = createCutoutRemoveArray();
      useDesignStore.setState({ additionRemoved: allCutoutAddition });
    }
    autoParams.current.offLoadingCutout = isAutomaticOffloadAdditionsEnable;
  }, [isAutomaticOffloadAdditionsEnable])

  useEffect(() => {
    setPropertyPanelTabsEnabled(gcodeStatus.percent == 0 && currentUserHasEditAccess());
  }, [gcodeStatus, useDesignStore.getState().designLoaded])


  const currentUserHasEditAccess = (): boolean => {
    const designCreator = useSelectedDesignStore.getState().createdBy;
    return canEditDesign(authenticatedUser, designCreator?.id);
  }

  const updateAutomaticParameters = () => {
    if (!leftMetatarsalBarInput.current) return;
    autoParamsInit.current = true;

    leftMetatarsalBarInput.current.checked = autoParams.current.metatarSalBarLeft != -1;
    rightMetatarsalBarInput.current!.checked = autoParams.current.metatarSalBarRight != -1;
    leftMetatarsalPadInput.current!.checked = autoParams.current.metatarSalPadLeft != -1;
    rightMetatarsalPadInput.current!.checked = autoParams.current.metatarSalPadRight != -1;
    leftMortonsExtensionInput.current!.checked = autoParams.current.mortonsExtensionLeft != -1;
    rightMortonsExtensionInput.current!.checked = autoParams.current.mortonsExtensionRight != -1;
    leftDancerPadInput.current!.checked = autoParams.current.dancersPadLeft != -1;
    rightDancerPadInput.current!.checked = autoParams.current.dancersPadRight != -1;

    rightMetatarsalgia.current!.checked = (autoParams.current.metatarsalgia === ConditionSide.both || autoParams.current.metatarsalgia === ConditionSide.right);
    rightHalluxLimitus.current!.checked = (autoParams.current.halluxLimitus === ConditionSide.both || autoParams.current.halluxLimitus === ConditionSide.right);
    rightSesamoiditis.current!.checked = (autoParams.current.sesamoiditis === ConditionSide.both || autoParams.current.sesamoiditis === ConditionSide.right);

    leftMetatarsalgia.current!.checked = (autoParams.current.metatarsalgia === ConditionSide.both || autoParams.current.metatarsalgia === ConditionSide.left);
    leftHalluxLimitus.current!.checked = (autoParams.current.halluxLimitus === ConditionSide.both || autoParams.current.halluxLimitus === ConditionSide.left);
    leftSesamoiditis.current!.checked = (autoParams.current.sesamoiditis === ConditionSide.both || autoParams.current.sesamoiditis === ConditionSide.left);

    setIsAutomaticOffloadAdditionsEnable(autoParams.current.offLoadingCutout);

    if (leftCutoutMet1.current) {
      updateCutoutAdditions();
    }

    setIsLimbLengthEnable(autoParams.current.limbLengthDiscrepancy);
    var limbFoot = (autoParams.current.shorterLimb === 'right') ? 'Right' : 'Left';
    setLimbLengthFoot(limbFoot);
    if (autoParams.current.DiscrepancyAmount != 0) {
      const discrepancy = convertMmToViewerUnit(autoParams.current.DiscrepancyAmount);
      setLimbLengthAmount(discrepancy);
    }

    autoParamsInit.current = false;
  }

  const updateCutoutAdditions = () => {
    if (leftCutoutMet1.current) leftCutoutMet1.current.checked = autoParams.current.cutoutMet1Left != -1;
    if (rightCutoutMet1.current) rightCutoutMet1.current.checked = autoParams.current.cutoutMet1Right != -1;
    if (leftCutoutMet2.current) leftCutoutMet2.current.checked = autoParams.current.cutoutMet2Left != -1;
    if (rightCutoutMet2.current) rightCutoutMet2.current.checked = autoParams.current.cutoutMet2Right != -1;
    if (leftCutoutMet3.current) leftCutoutMet3.current.checked = autoParams.current.cutoutMet3Left != -1;
    if (rightCutoutMet3.current) rightCutoutMet3.current.checked = autoParams.current.cutoutMet3Right != -1;
    if (leftCutoutMet4.current) leftCutoutMet4.current.checked = autoParams.current.cutoutMet4Left != -1;
    if (rightCutoutMet4.current) rightCutoutMet4.current.checked = autoParams.current.cutoutMet4Right != -1;
    if (leftCutoutMet5.current) leftCutoutMet5.current.checked = autoParams.current.cutoutMet5Left != -1;
    if (rightCutoutMet5.current) rightCutoutMet5.current.checked = autoParams.current.cutoutMet5Right != -1;
  }

  const createCutoutRemoveArray = () => {
    // const currentState = useDesignStore.getState();
    // if (currentState.insoleDesign == null) return;
    let cutoutAdditions = [];
    // const additionIndex = currentState.insoleDesign?.insoles[foot].additions.findIndex(add => add.designAdditionId === id);
    if (autoParams.current.cutoutMet1Right != -1) {
      cutoutAdditions.push({ side: 0, index: autoParams.current.cutoutMet1Right, isAdditionID: true });
      autoParams.current.cutoutMet1Right = -1;
    }
    if (autoParams.current.cutoutMet2Right != -1) {
      cutoutAdditions.push({ side: 0, index: autoParams.current.cutoutMet2Right, isAdditionID: true });
      autoParams.current.cutoutMet2Right = -1;
    }
    if (autoParams.current.cutoutMet3Right != -1) {
      cutoutAdditions.push({ side: 0, index: autoParams.current.cutoutMet3Right, isAdditionID: true });
      autoParams.current.cutoutMet3Right = -1;
    }
    if (autoParams.current.cutoutMet4Right != -1) {
      cutoutAdditions.push({ side: 0, index: autoParams.current.cutoutMet4Right, isAdditionID: true });
      autoParams.current.cutoutMet4Right = -1;
    }
    if (autoParams.current.cutoutMet5Right != -1) {
      cutoutAdditions.push({ side: 0, index: autoParams.current.cutoutMet5Right, isAdditionID: true });
      autoParams.current.cutoutMet5Right = -1;
    }

    if (autoParams.current.cutoutMet1Left != -1) {
      cutoutAdditions.push({ side: 1, index: autoParams.current.cutoutMet1Left, isAdditionID: true });
      autoParams.current.cutoutMet1Left = -1;
    }
    if (autoParams.current.cutoutMet2Left != -1) {
      cutoutAdditions.push({ side: 1, index: autoParams.current.cutoutMet2Left, isAdditionID: true });
      autoParams.current.cutoutMet2Left = -1;
    }
    if (autoParams.current.cutoutMet3Left != -1) {
      cutoutAdditions.push({ side: 1, index: autoParams.current.cutoutMet3Left, isAdditionID: true });
      autoParams.current.cutoutMet3Left = -1;
    }
    if (autoParams.current.cutoutMet4Left != -1) {
      cutoutAdditions.push({ side: 1, index: autoParams.current.cutoutMet4Left, isAdditionID: true });
      autoParams.current.cutoutMet4Left = -1;
    }
    if (autoParams.current.cutoutMet5Left != -1) {
      cutoutAdditions.push({ side: 1, index: autoParams.current.cutoutMet5Left, isAdditionID: true });
      autoParams.current.cutoutMet5Left = -1;
    }

    handleAutomaticCondition();
    return cutoutAdditions;
  }

  const handleLimbLengthDiscrepancy = (enable: boolean) => {
    if (autoParamsInit.current) return;
    autoParams.current.limbLengthDiscrepancy = enable;
    autoParams.current.shorterLimb = (limbLengthFoot == 'Right') ? 'right' : 'left';
    autoParams.current.DiscrepancyAmount = convertUnitToDefault(limbLengthAmount, getDefaultLengthUnit());
    const foot: number = limbLengthFoot == "Right" ? 0 : 1;
    const value = {
      value: {
        enable: enable,
        amount: limbLengthAmount
      }
    };
    handleSurfaceValueChange('Limb Length Discrepancy', foot, value, true);
  }

  const handleLimbLengthFootChange = (footSelected: string) => {
    if (autoParamsInit.current) return;
    if (!isLimbLengthEnable) return;
    // if (limbLengthFoot == null) return;
    autoParams.current.limbLengthDiscrepancy = isLimbLengthEnable;
    autoParams.current.shorterLimb = (footSelected == 'Right') ? 'right' : 'left';
    // autoParams.current.DiscrepancyAmount = limbLengthAmount; 

    const isRightFoot = footSelected === "Right"
    const rightValue = {
      value: {
        enable: isRightFoot,
        amount: limbLengthAmount
      }
    };

    const leftValue = {
      value: {
        enable: !isRightFoot,
        amount: limbLengthAmount
      }
    };

    handleSurfaceValueChange('Limb Length Discrepancy', 0, rightValue, false, leftValue);
  }

  useEffect(() => {
    if (autoParamsInit.current) return;
    if (metricChanged) {
      setMetricChanged(false);
      return;
    }

    const timerHandler = setTimeout(() => {
      const displacement = convertUnitToDefault(limbLengthAmount, getDefaultLengthUnit()) - autoParams.current.DiscrepancyAmount;
      if (displacement == 0 || !autoParams.current.limbLengthDiscrepancy) return;
      const foot: number = limbLengthFoot == "Right" ? 0 : 1;
      const pValue = {
        value: {
          enable: isLimbLengthEnable,
          amount: convertMmToViewerUnit(displacement)
        }
      };

      handleSurfaceValueChange('Limb Length Discrepancy', foot, pValue, true);
      autoParams.current.DiscrepancyAmount = convertUnitToDefault(limbLengthAmount, getDefaultLengthUnit());
      // handleAutomaticCondition();
    }, 500);

    return () => {
      clearTimeout(timerHandler);
    }
  }, [limbLengthAmount])

  const closeToolbox = () => {
    setIsContentVisible(false);
    if (isDesktop()) {
      setAdvancedIsActive(false);
      useViewerStore.setState({ advanceMode: false });
    }
  };

  useEffect(() => {
    document.addEventListener('toolbox:close', closeToolbox as EventListener);
    return () => document.removeEventListener('toolbox:close', closeToolbox as EventListener);
  }, []);

  const lengthExtraUpdates = (foot: number, newLength: number, single: boolean = true) => {
    if (!isSandal) return;

    const affectedFeet = single ? [foot] : [0, 1];
    affectedFeet.forEach((affectedFoot) => {
      const bandHeight = propertyValues[affectedFoot]?.bandHeight;
      if (typeof bandHeight !== 'number' || !Number.isFinite(bandHeight)) return;

      const minBandHeight = BandJoints.min * newLength;
      const maxBandHeight = BandJoints.max * newLength;
      const clampedBandHeight = Math.max(minBandHeight, Math.min(maxBandHeight, bandHeight));

      if (clampedBandHeight !== bandHeight) {
        handleSurfaceValueChange('Band Height', affectedFoot, clampedBandHeight);
      }
    });
  };


  const handleSurfaceValueChange = (fieldKey: string, foot: number, input: any, single: boolean = true, input2: any = null) => {
    var propertyName = fieldKey[0].toLowerCase() + fieldKey.substring(1);
    propertyName = propertyName.replaceAll(' ', '');

    let pvalue = input.value ?? input;
    if (input.min != undefined) {
      pvalue = parseFloat(input.value.toString());
    }

    setPropertyValues((prev) => {
      if (!prev[foot]) return prev;
      // Update the field by name (fieldKey) for the correct foot
      const updated = [...prev];
      updated[foot] = {
        ...prev[foot],
        [propertyName]: pvalue,
      };
      return updated;
    });

    const key = `${propertyName}:${foot}`;

    // Clear any previous timer
    if (timeoutRefs.current[key]) {
      clearTimeout(timeoutRefs.current[key]!);
    }

    timeoutRefs.current[key] = setTimeout(() => {

      if (propertyName == 'thickness' && getDefaultLengthUnit() == 'mm' && pvalue == 3)
        pvalue = 3.5;

      if (propertyName == 'thickness' && getDefaultLengthUnit() == 'inch' && pvalue == 0.12)
        pvalue = 0.14;

      // clamp primary value if needed
      if (input.min != undefined) {
        if (isNaN(pvalue)) pvalue = parseFloat(input.min);
        const intmin = parseFloat(input.min?.toString());
        const intmax = parseFloat(input.max?.toString());
        pvalue = Math.max(intmin, Math.min(intmax, pvalue));
      }

      if (!single) {
        let pvalue2 = pvalue
        if (input2 !== null) {
          pvalue2 = input2.value;
          if (input2.min != undefined) {
            pvalue2 = parseFloat(input2.value.toString());
          }

          if (input2.min != undefined) {
            if (isNaN(pvalue2)) pvalue2 = parseInt(input2.min);
            const intmin2 = parseInt(input2.min?.toString());
            const intmax2 = parseInt(input2.max?.toString());
            pvalue2 = Math.max(intmin2, Math.min(intmax2, pvalue));
          }
        }
        const event = new CustomEvent('updateSurfaceParameters', {
          detail: [{ fieldKey, foot, value: pvalue }, { fieldKey, foot: (foot + 1) % 2, value: pvalue2 }]
        });
        document.dispatchEvent(event);
      } else {
        const event = new CustomEvent('updateSurfaceParameters', {
          detail: { fieldKey, foot, value: pvalue, single },
        });
        document.dispatchEvent(event);
      }

      const boxUpdateEvent = new CustomEvent("updateClippingBounds");
      document.dispatchEvent(boxUpdateEvent);

      // if (propertyName == 'length') lengthExtraUpdates(foot, pvalue, true);
    }, 500);
  };

  const handleWedgeTypeChange = (fieldKey: string, foot: number, input: any, single: boolean = true, input2: any = null) => {
    const wedgeTypePosition = fieldKey.split(' ').at(0);
    if (wedgeTypePosition === 'Heel') {
      if (foot === 0) {
        if (input.value === 'Medial') {
          setRightHeelLateralWedgeEditEnabled(false);
          setRightHeelMedialWedgeEditEnabled(true);
        } else if (input.value === 'Lateral') {
          setRightHeelLateralWedgeEditEnabled(true);
          setRightHeelMedialWedgeEditEnabled(false);
        } else {
          setRightHeelLateralWedgeEditEnabled(true);
          setRightHeelMedialWedgeEditEnabled(true);
        }
      } else {
        if (input.value === 'Medial') {
          setLeftHeelLateralWedgeEditEnabled(false);
          setLeftHeelMedialWedgeEditEnabled(true);
        } else if (input.value === 'Lateral') {
          setLeftHeelLateralWedgeEditEnabled(true);
          setLeftHeelMedialWedgeEditEnabled(false);
        } else {
          setLeftHeelLateralWedgeEditEnabled(true);
          setLeftHeelMedialWedgeEditEnabled(true);
        }
      }
    } else if (wedgeTypePosition === 'Forefoot') {
      if (foot === 0) {
        if (input.value === 'Medial') {
          setRightForeFootLateralWedgeEditEnabled(false);
          setRightForeFootMedialWedgeEditEnabled(true);
        } else if (input.value === 'Lateral') {
          setRightForeFootLateralWedgeEditEnabled(true);
          setRightForeFootMedialWedgeEditEnabled(false);
        } else {
          setRightForeFootLateralWedgeEditEnabled(true);
          setRightForeFootMedialWedgeEditEnabled(true);
        }
      } else {
        if (input.value === 'Medial') {
          setLeftForeFootLateralWedgeEditEnabled(false);
          setLeftForeFootMedialWedgeEditEnabled(true);
        } else if (input.value === 'Lateral') {
          setLeftForeFootLateralWedgeEditEnabled(true);
          setLeftForeFootMedialWedgeEditEnabled(false);
        } else {
          setLeftForeFootLateralWedgeEditEnabled(true);
          setLeftForeFootMedialWedgeEditEnabled(true);
        }
      }
    }
    handleSurfaceValueChange(fieldKey, foot, input, single, input2);
  }

  // Cleanup the timer on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRefs.current[0])
        clearTimeout(timeoutRefs.current[0]);
      if (timeoutRefs.current[1])
        clearTimeout(timeoutRefs.current[1]);

      const tableBody = document.querySelector('#additions tbody');
      tableBody?.remove();
    };
  }, []);

  const handleAutomaticCondition = async () => {
    // if (autoDesignTimeoutRef.current) {
    //   clearTimeout(autoDesignTimeoutRef.current);
    // }

    // autoDesignTimeoutRef.current = setTimeout(() => {
    const design = useDesignStore.getState().insoleDesign;
    if (design == null) return;
    // const designId = useSelectedDesignStore.getState().designId;
    design.autoProperties = autoParams.current;
    // saveAutoDesignParameters(api, design, designId);
    // autoDesignTimeoutRef.current = null;
    // }, 100);
    // await scheduleAutosave("", { updateAutoProperties: true });
  }

  const handleInsoleTemplates = async (targetValue: any) => {
    if (autoParamsInit.current) return;
    const nextTemplate = (targetValue?.value ?? targetValue) as InsoleType;
    autoParams.current.insoleTemplate = nextTemplate;
    handleSurfaceValueChange('Insole Template', 0, targetValue, false);
  }

  const handleProfileCurve = async (input: HTMLSelectElement) => {
    if (autoParamsInit.current) return;

    handleSurfaceValueChange('Profile Curve', 0, input, false);
    // handleSurfaceValueChange('Profile Curve', 1, targetValue);
    // autoParams.current.profileCurveType = input.value as ProfileCurveTemplates;
    // handleAutomaticCondition();
  }

  const handleMetatarsalgia = (right: boolean, left: boolean) => {
    if (right && left) autoParams.current.metatarsalgia = ConditionSide.both;
    else if (right) autoParams.current.metatarsalgia = ConditionSide.right;
    else if (left) autoParams.current.metatarsalgia = ConditionSide.left;
    else autoParams.current.metatarsalgia = ConditionSide.none;
  }

  const handleRightMetatarsalgia = (checked: boolean) => {
    if (autoParamsInit.current) return;
    if (rightMetatarsalPadInput.current == null || rightMetatarsalgia.current == null) return;
    handleMetatarsalgia(rightMetatarsalgia.current.checked, leftMetatarsalgia.current.checked);
    if (checked == rightMetatarsalPadInput.current.checked) {
      handleAutomaticCondition();
      return;
    }
    handleAutomaticAdditionAdder(checked, "MP Pad 1", 'right');
    rightMetatarsalPadInput.current.checked = checked;
  }

  const handleLeftMetatarsalgia = (checked: boolean) => {
    if (autoParamsInit.current) return;
    if (leftMetatarsalPadInput.current == null || leftMetatarsalgia.current == null) return;
    handleMetatarsalgia(rightMetatarsalgia.current.checked, leftMetatarsalgia.current.checked);
    if (checked == leftMetatarsalPadInput.current.checked) {
      handleAutomaticCondition();
      return;
    }
    handleAutomaticAdditionAdder(checked, "MP Pad 1", 'left');
    leftMetatarsalPadInput.current.checked = checked;
  }

  const handleHalluxLimitus = (right: boolean, left: boolean) => {
    if (right && left) autoParams.current.halluxLimitus = ConditionSide.both;
    else if (right) autoParams.current.halluxLimitus = ConditionSide.right;
    else if (left) autoParams.current.halluxLimitus = ConditionSide.left;
    else autoParams.current.halluxLimitus = ConditionSide.none;
  }

  const handleRightHalluxLimitus = (checked: boolean) => {
    if (autoParamsInit.current) return;
    if (rightMortonsExtensionInput.current == null || rightHalluxLimitus.current == null) return;
    handleHalluxLimitus(rightHalluxLimitus.current.checked, leftHalluxLimitus.current.checked);
    if (checked == rightMortonsExtensionInput.current.checked) {
      handleAutomaticCondition();
      return;
    }
    handleAutomaticAdditionAdder(checked, "Morton's Extention", 'right');
    rightMortonsExtensionInput.current.checked = checked;
  }

  const handleLeftHalluxLimitus = (checked: boolean) => {
    if (autoParamsInit.current) return;
    if (leftMortonsExtensionInput.current == null || leftHalluxLimitus.current == null) return;
    handleHalluxLimitus(rightHalluxLimitus.current.checked, leftHalluxLimitus.current.checked);
    if (checked == leftMortonsExtensionInput.current.checked) {
      handleAutomaticCondition();
      return;
    }
    handleAutomaticAdditionAdder(checked, "Morton's Extention", 'left');
    leftMortonsExtensionInput.current.checked = checked;
  }

  const handleSesamoiditis = (right: boolean, left: boolean) => {
    if (right && left) autoParams.current.sesamoiditis = ConditionSide.both;
    else if (right) autoParams.current.sesamoiditis = ConditionSide.right;
    else if (left) autoParams.current.sesamoiditis = ConditionSide.left;
    else autoParams.current.sesamoiditis = ConditionSide.none;
  }

  const handleRightSesamoiditis = (checked: boolean) => {
    if (autoParamsInit.current) return;
    if (rightDancerPadInput.current == null || rightSesamoiditis.current == null) return;
    handleSesamoiditis(rightSesamoiditis.current.checked, leftSesamoiditis.current.checked);
    if (checked == rightDancerPadInput.current.checked) {
      handleAutomaticCondition();
      return;
    }
    handleAutomaticAdditionAdder(checked, 'Pad_2', 'right');
    rightDancerPadInput.current.checked = checked;
  }

  const handleLeftSesamoiditis = (checked: boolean) => {
    if (autoParamsInit.current) return;
    if (leftDancerPadInput.current == null || leftSesamoiditis.current == null) return;
    handleSesamoiditis(rightSesamoiditis.current.checked, leftSesamoiditis.current.checked);
    if (checked == leftDancerPadInput.current.checked) {
      handleAutomaticCondition();
      return;
    }
    handleAutomaticAdditionAdder(checked, 'Pad_2', 'left');
    leftDancerPadInput.current.checked = checked;
  }

  const calculateAutomaticAdditionPos = (foot: number, additionName: string, additionMesh: THREE.Mesh) => {
    let pressureDataExists = false;
    const insoleDesign = useDesignStore.getState().insoleDesign;
    if (insoleDesign == null) return;
    // if (pressureDataExists) {

    // } else {
    insoleDesign.insoles[foot].mainMesh.geometry.computeBoundingBox();
    additionMesh.geometry.computeBoundingBox();
    const insoleMin = insoleDesign.insoles[foot].mainMesh.geometry.boundingBox.min;
    const insoleMax = insoleDesign.insoles[foot].mainMesh.geometry.boundingBox.max;
    const metatarsalsXCoefs = [0.9, 0.7, 0.5, 0.3, 0.1];
    const metatarsalsZCoefs = [0.29, 0.29, 0.3, 0.31, 0.32];

    const Metp0 = new THREE.Vector3((insoleMax.x - insoleMin.x) * 0.5 + insoleMin.x, 0, (insoleMax.z - insoleMin.z) * 0.35 + insoleMin.z);
    let actualPoint;
    switch (additionName) {
      case 'MP Pad 1': {
        actualPoint = new THREE.Vector3(Metp0.x, 0, Metp0.z - additionMesh.geometry.boundingBox.min.z + 15);
        // actualPoint = m5.clone();
        break;
      }
      case 'MB 1-5 1': {
        const insoleCenter = insoleMax.clone().add(insoleMin).divideScalar(2);
        actualPoint = new THREE.Vector3(insoleCenter.x, 0, Metp0.z - additionMesh.geometry.boundingBox.min.z + 15);
        break;
      }
      case 'MPTm1': {
        actualPoint = new THREE.Vector3((insoleMax.x - insoleMin.x) * metatarsalsXCoefs[0] + insoleMin.x, 0, (insoleMax.z - insoleMin.z) * metatarsalsZCoefs[0] + insoleMin.z);
        break;
      }
      case 'MPTm2': {
        actualPoint = new THREE.Vector3((insoleMax.x - insoleMin.x) * metatarsalsXCoefs[1] + insoleMin.x, 0, (insoleMax.z - insoleMin.z) * metatarsalsZCoefs[1] + insoleMin.z);
        break;
      }
      case 'MPTm3': {
        actualPoint = new THREE.Vector3((insoleMax.x - insoleMin.x) * metatarsalsXCoefs[2] + insoleMin.x, 0, (insoleMax.z - insoleMin.z) * metatarsalsZCoefs[2] + insoleMin.z);
        break;
      }
      case 'MPTm4': {
        actualPoint = new THREE.Vector3((insoleMax.x - insoleMin.x) * metatarsalsXCoefs[3] + insoleMin.x, 0, (insoleMax.z - insoleMin.z) * metatarsalsZCoefs[3] + insoleMin.z);
        break;
      }
      case 'MPTm5': {
        actualPoint = new THREE.Vector3((insoleMax.x - insoleMin.x) * metatarsalsXCoefs[4] + insoleMin.x, 0, (insoleMax.z - insoleMin.z) * metatarsalsZCoefs[4] + insoleMin.z);
        break;
      }
    }
    // }

    const insoleSize = insoleMax.clone().sub(insoleMin);
    return new THREE.Vector3((actualPoint.x - insoleMin.x) / insoleSize.x, 0, (actualPoint.z - insoleMin.z) / insoleSize.z);
  }

  const handleAutoAdditionHardnessChanged = (autoDesignId: number, newVal: string) => {
    if (autoDesignId == -1) return;
    const currentState = useDesignStore.getState();
    if (currentState.insoleDesign == null) return;

    // let addId = currentState.insoleDesign?.insoles[foot].additions.findIndex(add => add.designAdditionId === autoDesignId);
    // allAdditions.map(({ item, insoleSide, insoleIndex, index })
    let selectedAdd = allAdditions.find((element) => element.item.designAdditionId === autoDesignId);
    if (!selectedAdd) return;
    handleAdditionHardnessChange(selectedAdd.item, selectedAdd.index, newVal);
  }

  const updateAutoAdditionHardness = (foot: number, autoDesignId: number): string => {
    if (autoDesignId == -1) return Hardness.Medium;
    const currentState = useDesignStore.getState();
    if (currentState.insoleDesign == null) return Hardness.Medium;

    let addId = currentState.insoleDesign?.insoles[foot].additions.findIndex(add => add.designAdditionId === autoDesignId);
    if (addId == -1) return Hardness.Medium;
    else return currentState.insoleDesign?.insoles[foot].additions[addId].hardness;
  }

  const showLoading = () => {
    const event = new CustomEvent('setLoadingEnable', {});
    document.dispatchEvent(event);
  }

  const hideLoading = () => {
    const event = new CustomEvent('setLoadingDisable', {});
    document.dispatchEvent(event);
  }

  const handleAutomaticAdditionAdder = async (checked: boolean, additionName: string, side: 'right' | 'left') => {
    const foot = side === 'right' ? 0 : 1;
    if (checked) {
      showLoading();
      const addition = await addNewAddition(api, additionName, foot)
      if (addition == null) {
        hideLoading();
        return;
      }
      switch (additionName) {
        case 'MP Pad 1': {
          if (foot == 0) autoParams.current.metatarSalPadRight = 0;
          else autoParams.current.metatarSalPadLeft = 0;
          addition.defaultRelativeLocation = calculateAutomaticAdditionPos(foot, additionName, addition.mesh)!;
          addition.automaticAddition = AutomaticAdditions.MetatarsalPad;
          addition.SetHeight(6); break;
        }
        case "Morton's Extention": {
          if (foot == 0) autoParams.current.mortonsExtensionRight = 0;
          else autoParams.current.mortonsExtensionLeft = 0;
          addition.automaticAddition = AutomaticAdditions.MortonExtention;
          addition.SetHeight(1); break;
        }
        case 'Pad_2': {
          if (foot == 0) autoParams.current.dancersPadRight = 0;
          else autoParams.current.dancersPadLeft = 0;
          addition.automaticAddition = AutomaticAdditions.DancerPad;
          addition.SetHeight(6); break;
        }
        case 'MB 1-5 1': {
          if (foot == 0) autoParams.current.metatarSalBarRight = 0;
          else autoParams.current.metatarSalBarLeft = 0;
          addition.defaultRelativeLocation = calculateAutomaticAdditionPos(foot, additionName, addition.mesh)!;
          addition.automaticAddition = AutomaticAdditions.MetatarsalBar;
          addition.SetHeight(6); break;
        }
      }
      useDesignStore.getState().pushAdditionToDesign(addition, foot);
      hideLoading();

    } else {
      const currentState = useDesignStore.getState();
      if (currentState.insoleDesign == null) return;
      // autoParams.current = currentState.insoleDesign.autoProperties;
      const id = findAutoAdditionId(additionName, foot);
      if (id === -1) return;

      const additionIndex = currentState.insoleDesign?.insoles[foot].additions.findIndex(add => add.designAdditionId === id);
      if (id === null || id === undefined) return;
      useDesignStore.setState({ additionRemoved: [{ 'side': foot, 'index': additionIndex, 'isAdditionID': false }] });

      updateAutoAdditionAfterRemove(additionName, foot);
    }
  }

  const findAutoAdditionId = (additionName: string, foot: number): number => {
    let additionId = 0;
    switch (additionName) {
      case 'MP Pad 1': {
        if (foot == 0) additionId = autoParams.current.metatarSalPadRight;
        else additionId = autoParams.current.metatarSalPadLeft;
        break;
      }
      case "Morton's Extention": {
        if (foot == 0) additionId = autoParams.current.mortonsExtensionRight;
        else additionId = autoParams.current.mortonsExtensionLeft;
        break;
      }
      case 'Pad_2': {
        if (foot == 0) additionId = autoParams.current.dancersPadRight;
        else additionId = autoParams.current.dancersPadLeft;
        break;
      }
      case 'MB 1-5 1': {
        if (foot == 0) additionId = autoParams.current.metatarSalBarRight;
        else additionId = autoParams.current.metatarSalBarLeft;
        break;
      }
    }
    return additionId;
  }

  const updateAutoAdditionAfterRemove = (additionName: string, foot: number) => {
    switch (additionName) {
      case 'MP Pad 1': {
        if (foot == 0) autoParams.current.metatarSalPadRight = -1;
        else autoParams.current.metatarSalPadLeft = -1;
        break;
      }
      case "Morton's Extention": {
        if (foot == 0) autoParams.current.mortonsExtensionRight = -1;
        else autoParams.current.mortonsExtensionLeft = -1;
        break;
      }
      case 'Pad_2': {
        if (foot == 0) autoParams.current.dancersPadRight = -1;
        else autoParams.current.dancersPadLeft = -1;
        break;
      }
      case 'MB 1-5 1': {
        if (foot == 0) autoParams.current.metatarSalBarRight = -1;
        else autoParams.current.metatarSalBarLeft = -1;
        break;
      }
    }
    //handleAutomaticCondition();
  }

  const conditionSideOn = (side: ConditionSide | string | undefined, which: "left" | "right") => {
    const value = String(side || "").toLowerCase();
    return value === which || value === "both";
  };

  voiceAutomaticRef.current = async (detail: any) => {
    const condition = String(detail?.condition || "").toLowerCase().replace(/[\s_-]/g, "");
    const footRaw = String(detail?.foot || "both").toLowerCase();
    const sides: Array<"left" | "right"> =
      footRaw === "left" ? ["left"] : footRaw === "right" ? ["right"] : ["left", "right"];
    const on = Boolean(detail?.on);

    if (condition === "shoelast" || condition === "profilecurve" || condition === "last") {
      if (useDesignStore.getState().insoleDesign?.isSandal) {
        throw new Error("Shoe Last is not available on a sandal. Switch the scene model to insole first.");
      }
      const last = resolveProfileCurveName(String(detail?.value || ""));
      if (!last) {
        throw new Error("Unknown shoe last. Use Generic, Classic, Durea, Sneaker, Men, Women, or Darco.");
      }
      const selectValue = profileCurveSelectKey(last);
      autoParams.current.profileCurveRight = last;
      autoParams.current.profileCurveLeft = last;
      const bothFeet = sides.length === 2;
      if (bothFeet || sides.includes("right")) {
        handleSurfaceValueChange("Profile Curve", 0, { value: selectValue }, !bothFeet && !sides.includes("left"));
      }
      if (!bothFeet && sides.includes("left")) {
        handleSurfaceValueChange("Profile Curve", 1, { value: selectValue }, true);
      }
      await new Promise((resolve) => window.setTimeout(resolve, 700));
      handleAutomaticCondition();
      return;
    }

    if (condition === "offloadingcutout" || condition === "offloadcutout" || condition === "offloading") {
      setIsAutomaticOffloadAdditionsEnable(on);
      autoParams.current.offLoadingCutout = on;
      if (!on) {
        const allCutoutAddition = createCutoutRemoveArray();
        if (allCutoutAddition.length) useDesignStore.setState({ additionRemoved: allCutoutAddition });
      }
      handleAutomaticCondition();
      return;
    }

    const cutoutMet = condition.match(/^cutoutmet([1-5])$/);
    if (cutoutMet) {
      const metNumber = Number(cutoutMet[1]);
      if (on && !isAutomaticOffloadAdditionsEnable) {
        setIsAutomaticOffloadAdditionsEnable(true);
        autoParams.current.offLoadingCutout = true;
        await new Promise((resolve) => window.setTimeout(resolve, 80));
      }
      const cutoutRefs: Record<number, { left: React.RefObject<HTMLInputElement>; right: React.RefObject<HTMLInputElement> }> = {
        1: { left: leftCutoutMet1, right: rightCutoutMet1 },
        2: { left: leftCutoutMet2, right: rightCutoutMet2 },
        3: { left: leftCutoutMet3, right: rightCutoutMet3 },
        4: { left: leftCutoutMet4, right: rightCutoutMet4 },
        5: { left: leftCutoutMet5, right: rightCutoutMet5 },
      };
      for (const side of sides) {
        const footIndex = side === "right" ? 0 : 1;
        const already = findCutoutAutoAdditionId(metNumber, footIndex) !== -1;
        if (on !== already) {
          await handleAutomaticDesignCutoutAddition(on, side, metNumber);
        }
        const box = cutoutRefs[metNumber]?.[side];
        if (box?.current) box.current.checked = on;
      }
      handleAutomaticCondition();
      return;
    }

    const applyLimbLength = (enable: boolean, shorterLabel: "Left" | "Right", amount: number) => {
      // The viewer adds the amount it is given to the heel height, it does not set it,
      // so every update sends the difference from what is already on the model, the way
      // the spinner does. autoParams is what the model currently carries.
      const wasEnabled = autoParams.current.limbLengthDiscrepancy;
      const previousFoot = autoParams.current.shorterLimb === "right" ? 0 : 1;
      const previousAmount = convertMmToViewerUnit(autoParams.current.DiscrepancyAmount);
      const footIndex = shorterLabel === "Right" ? 0 : 1;

      setIsLimbLengthEnable(enable);
      setLimbLengthFoot(shorterLabel);
      setLimbLengthAmount(amount);
      autoParams.current.limbLengthDiscrepancy = enable;
      autoParams.current.shorterLimb = shorterLabel === "Right" ? "right" : "left";
      autoParams.current.DiscrepancyAmount = convertUnitToDefault(amount, getDefaultLengthUnit());

      // Take the old lift off the foot that was carrying it before lifting another one.
      if (wasEnabled && (!enable || previousFoot !== footIndex)) {
        handleSurfaceValueChange("Limb Length Discrepancy", previousFoot, { value: { enable: false, amount: previousAmount } }, true);
      }
      if (!enable) return;

      const sameFoot = wasEnabled && previousFoot === footIndex;
      const displacement = sameFoot ? amount - previousAmount : amount;
      if (sameFoot && displacement === 0) return;
      handleSurfaceValueChange("Limb Length Discrepancy", footIndex, { value: { enable: true, amount: displacement } }, true);
    };

    if (condition === "limblengthdiscrepancy" || condition === "limblength" || condition === "lld") {
      const amountRaw = detail?.value;
      const amountNum = amountRaw === undefined || amountRaw === "" || amountRaw === "on" || amountRaw === "off"
        ? undefined
        : Number(amountRaw);
      const enable = Number.isFinite(amountNum) ? true : on;
      const shorter: "Left" | "Right" = footRaw === "left" ? "Left" : footRaw === "right" ? "Right" : (limbLengthFoot === "Left" ? "Left" : "Right");
      const amount = Number.isFinite(amountNum)
        ? Math.max(convertMmToViewerUnit(0), Math.min(convertMmToViewerUnit(20), Number(amountNum)))
        : limbLengthAmount;
      applyLimbLength(enable, shorter, amount);
      await new Promise((resolve) => window.setTimeout(resolve, 600));
      handleAutomaticCondition();
      return;
    }

    if (condition === "shorterlimb") {
      const shorter: "Left" | "Right" = footRaw === "left" || String(detail?.value || "").toLowerCase().startsWith("l") ? "Left" : "Right";
      applyLimbLength(true, shorter, limbLengthAmount);
      await new Promise((resolve) => window.setTimeout(resolve, 600));
      handleAutomaticCondition();
      return;
    }

    if (condition === "discrepancyamount" || condition === "limblengthamount") {
      const amountNum = Number(detail?.value);
      if (!Number.isFinite(amountNum)) {
        throw new Error("discrepancyAmount must be a number.");
      }
      const clamped = Math.max(convertMmToViewerUnit(0), Math.min(convertMmToViewerUnit(20), amountNum));
      const shorter: "Left" | "Right" = footRaw === "left" ? "Left" : footRaw === "right" ? "Right" : (limbLengthFoot === "Left" ? "Left" : "Right");
      applyLimbLength(true, shorter, clamped);
      await new Promise((resolve) => window.setTimeout(resolve, 600));
      handleAutomaticCondition();
      return;
    }

    if (condition === "foottype" || condition === "insoletemplate") {
      const type = resolveInsoleTemplateApiName(detail?.value);
      if (type) {
        autoParams.current.insoleTemplate = type as InsoleType;
        handleInsoleTemplates({ value: type });
        await new Promise((resolve) => window.setTimeout(resolve, 600));
      }
      handleAutomaticCondition();
      return;
    }

    const spec =
      condition === "metatarsalgia" ? { name: "MP Pad 1", flag: "metatarsalgia" as const }
        : condition === "halluxlimitus" ? { name: "Morton's Extention", flag: "halluxLimitus" as const }
          : condition === "sesamoiditis" ? { name: "Pad_2", flag: "sesamoiditis" as const }
            : condition === "metatarsalbar" ? { name: "MB 1-5 1", flag: null }
              : null;
    if (!spec) return;

    for (const side of sides) {
      const footIndex = side === "right" ? 0 : 1;
      const insoles = useDesignStore.getState().insoleDesign?.insoles;
      const hasNamed = (insoles?.[footIndex]?.additions || []).some(
        (a) => (a.name || a.fileName) === spec.name && a.status !== AdditionNewStatus.removed
      );
      const already = findAutoAdditionId(spec.name, footIndex) !== -1 || hasNamed;
      if (on !== already) {
        await handleAutomaticAdditionAdder(on, spec.name, side);
      }
      if (spec.name === "MP Pad 1") {
        if (side === "left" && leftMetatarsalPadInput.current) leftMetatarsalPadInput.current.checked = on;
        if (side === "right" && rightMetatarsalPadInput.current) rightMetatarsalPadInput.current.checked = on;
        if (side === "left" && leftMetatarsalgia.current) leftMetatarsalgia.current.checked = on;
        if (side === "right" && rightMetatarsalgia.current) rightMetatarsalgia.current.checked = on;
      }
      if (spec.name === "Morton's Extention") {
        if (side === "left" && leftMortonsExtensionInput.current) leftMortonsExtensionInput.current.checked = on;
        if (side === "right" && rightMortonsExtensionInput.current) rightMortonsExtensionInput.current.checked = on;
        if (side === "left" && leftHalluxLimitus.current) leftHalluxLimitus.current.checked = on;
        if (side === "right" && rightHalluxLimitus.current) rightHalluxLimitus.current.checked = on;
      }
      if (spec.name === "Pad_2") {
        if (side === "left" && leftDancerPadInput.current) leftDancerPadInput.current.checked = on;
        if (side === "right" && rightDancerPadInput.current) rightDancerPadInput.current.checked = on;
        if (side === "left" && leftSesamoiditis.current) leftSesamoiditis.current.checked = on;
        if (side === "right" && rightSesamoiditis.current) rightSesamoiditis.current.checked = on;
      }
      if (spec.name === "MB 1-5 1") {
        if (side === "left" && leftMetatarsalBarInput.current) leftMetatarsalBarInput.current.checked = on;
        if (side === "right" && rightMetatarsalBarInput.current) rightMetatarsalBarInput.current.checked = on;
      }
    }

    if (spec.flag === "metatarsalgia") {
      handleMetatarsalgia(
        sides.includes("right") ? on : conditionSideOn(autoParams.current.metatarsalgia, "right"),
        sides.includes("left") ? on : conditionSideOn(autoParams.current.metatarsalgia, "left")
      );
    } else if (spec.flag === "halluxLimitus") {
      handleHalluxLimitus(
        sides.includes("right") ? on : conditionSideOn(autoParams.current.halluxLimitus, "right"),
        sides.includes("left") ? on : conditionSideOn(autoParams.current.halluxLimitus, "left")
      );
    } else if (spec.flag === "sesamoiditis") {
      handleSesamoiditis(
        sides.includes("right") ? on : conditionSideOn(autoParams.current.sesamoiditis, "right"),
        sides.includes("left") ? on : conditionSideOn(autoParams.current.sesamoiditis, "left")
      );
    }
    handleAutomaticCondition();
  };

  const handleAutomaticDesignCutoutAddition = async (checked: boolean, side: 'right' | 'left', metNumber: number) => {
    const foot = side === 'right' ? 0 : 1;
    if (checked) {
      showLoading();

      const addition = await addNewAddition(api, 'Cut-Out', foot)
      if (addition == null) {
        hideLoading();
        return;
      }

      addition.status = AdditionNewStatus.isNew;
      addition.defaultRelativeLocation = calculateAutomaticAdditionPos(foot, `MPTm${metNumber}`, addition.mesh)!;
      addition.SetHeight(3);
      switch (metNumber) {
        case 1: {
          if (foot == 0) autoParams.current.cutoutMet1Right = 0;
          else autoParams.current.cutoutMet1Left = 0;
          addition.automaticAddition = AutomaticAdditions.CutoutMet1; break;
        }
        case 2: {
          if (foot == 0) autoParams.current.cutoutMet2Right = 0;
          else autoParams.current.cutoutMet2Left = 0;
          addition.automaticAddition = AutomaticAdditions.CutoutMet2; break;
        }
        case 3: {
          if (foot == 0) autoParams.current.cutoutMet3Right = 0;
          else autoParams.current.cutoutMet3Left = 0;
          addition.automaticAddition = AutomaticAdditions.CutoutMet3; break;
        }
        case 4: {
          if (foot == 0) autoParams.current.cutoutMet4Right = 0;
          else autoParams.current.cutoutMet4Left = 0;
          addition.automaticAddition = AutomaticAdditions.CutoutMet4; break;
        }
        case 5: {
          if (foot == 0) autoParams.current.cutoutMet5Right = 0;
          else autoParams.current.cutoutMet5Left = 0;
          addition.automaticAddition = AutomaticAdditions.CutoutMet5; break;
        }
      }
      useDesignStore.getState().pushAdditionToDesign(addition, foot);
      hideLoading();
    } else {
      const currentState = useDesignStore.getState();
      if (currentState.insoleDesign == null) return;
      // autoParams.current = currentState.insoleDesign.autoProperties;

      const id = findCutoutAutoAdditionId(metNumber, foot);
      if (id === -1) return;

      const additionIndex = currentState.insoleDesign?.insoles[foot].additions.findIndex(add => add.designAdditionId === id);

      const designid = useSelectedDesignStore.getState().designId;
      if (id === null || id === undefined) return;
      //await removeOneAddition(api, designid, id);
      useDesignStore.setState({ additionRemoved: [{ 'side': foot, 'index': additionIndex, 'isAdditionID': false }] });
      updateCutoutAutoAdditionAfterRemove(metNumber, foot);
    }
  }

  const findCutoutAutoAdditionId = (cutoutNumber: number, foot: number): number => {
    let additionId = 0;
    switch (cutoutNumber) {
      case 1: {
        if (foot == 0) additionId = autoParams.current.cutoutMet1Right;
        else additionId = autoParams.current.cutoutMet1Left;
        break;
      }
      case 2: {
        if (foot == 0) additionId = autoParams.current.cutoutMet2Right;
        else additionId = autoParams.current.cutoutMet2Left;
        break;
      }
      case 3: {
        if (foot == 0) additionId = autoParams.current.cutoutMet3Right;
        else additionId = autoParams.current.cutoutMet3Left;
        break;
      }
      case 4: {
        if (foot == 0) additionId = autoParams.current.cutoutMet4Right;
        else additionId = autoParams.current.cutoutMet4Left;
        break;
      }
      case 5: {
        if (foot == 0) additionId = autoParams.current.cutoutMet5Right;
        else additionId = autoParams.current.cutoutMet5Left;
        break;
      }
    }
    return additionId;
  }

  const updateCutoutAutoAdditionAfterRemove = (cutoutNumber: number, foot: number) => {
    switch (cutoutNumber) {
      case 1: {
        if (foot == 0) autoParams.current.cutoutMet1Right = -1;
        else autoParams.current.cutoutMet1Left = -1;
        break;
      }
      case 2: {
        if (foot == 0) autoParams.current.cutoutMet2Right = -1;
        else autoParams.current.cutoutMet2Left = -1;
        break;
      }
      case 3: {
        if (foot == 0) autoParams.current.cutoutMet3Right = -1;
        else autoParams.current.cutoutMet3Left = -1;
        break;
      }
      case 4: {
        if (foot == 0) autoParams.current.cutoutMet4Right = -1;
        else autoParams.current.cutoutMet4Left = -1;
        break;
      }
      case 5: {
        if (foot == 0) autoParams.current.cutoutMet5Right = -1;
        else autoParams.current.cutoutMet5Left = -1;
        break;
      }
    }
    handleAutomaticCondition();
  }

  const handleNewAdditionDoubleClicked = async (additionName: string) => {
    setSelectedAdditionImage(additionName);
    showLoading();

    const addRight = await addNewAddition(api, additionName, 0);
    if (addRight == null) { hideLoading(); return; }
    useDesignStore.getState().pushAdditionToDesign(addRight, 0);

    const addLeft = await addNewAddition(api, additionName, 1);
    if (addLeft == null) { hideLoading(); return; }
    useDesignStore.getState().pushAdditionToDesign(addLeft, 1);


    hideLoading();
  };

  const handleNewAdditionClicked = async (additionName: string) => {
    if (isMobile() || isTablet()) {
      handleNewAdditionDoubleClicked(additionName);
    }
  }

  const handleHeightAdditionChanged = (item: Addition, index: number, newVal: string) => {
    if (additionHeightTimeoutRef.current) {
      clearTimeout(additionHeightTimeoutRef.current);
    }

    let newHeight: number = convertUnitToDefault(parseFloat(newVal), getDefaultLengthUnit());
    const footAdditionHeightIndex = item.foot + index;
    if (!isNaN(newHeight)) {
      setAdditionHeights(prev => ({ ...prev, [footAdditionHeightIndex]: newHeight.toString() }));
    }

    additionHeightTimeoutRef.current = setTimeout(() => {
      if (isNaN(newHeight)) newHeight = item.height.min;
      if (newHeight > item.height.max) newHeight = item.height.max;
      if (newHeight < item.height.min) newHeight = item.height.min;
      // item.height.setValue(newHeight);
      useDesignStore.setState({ additionHeightUpdated: { side: item.foot == 'right' ? 0 : 1, index: index, value: newHeight } })
      useViewerStore.getState().setSelectedAdditionFromViewer(item.foot === 'right' ? 0 : 1, index);

      setAdditionHeights(prev => {
        const newState = { ...prev };
        delete newState[footAdditionHeightIndex];
        return newState;
      });
    }, 500);

  }

  const handleAdditionHardnessChange = (item: Addition, index: number, newValue: string) => {
    useDesignStore.setState({ additionHardnessUpdated: { side: item.foot == 'right' ? 0 : 1, index: index, value: newValue as Hardness } })
    if (finalize) useDesignStore.setState({ GcodeReady: false });
  };

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (
          selectedAdditionIndex !== null &&
          selectedAdditionIndex !== undefined &&
          selectedFoot !== null &&
          selectedFoot !== undefined
        ) {
          handleRemoveAdditionClicked(selectedFoot, selectedAdditionIndex);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedAdditionIndex, selectedFoot]);


  const handleRemoveAdditionClicked = async (side: number, additionIndex: number) => {
    if (insoleDesign == null) return;
    if (additionIndex < 0 || side < 0) return;
    // const currentState = useDesignStore.getState();

    const currentAdditions = insoleDesign?.insoles[side].additions.filter(a => a.status != AdditionNewStatus.removed);
    const addition = currentAdditions[additionIndex];
    const id = addition?.designAdditionId;
    if (id === null || id === undefined) return;
    useDesignStore.setState({ additionRemoved: [{ 'side': side, 'index': additionIndex, 'isAdditionID': false }] });
  }

  const toggleAdvance = () => {
    useViewerStore.getState().toggleAdvance();
    setAdvancedIsActive((prev) => !prev);
  }

  useEffect(() => {
    const loadImages = async () => {
      let imageNames = useSelectedDesignStore.getState().additionListNamesFromServer;
      if (imageNames != null && imageNames.length !== 0) {
        setAdditionImagesFilename(imageNames);
      }
    };

    loadImages();
  }, [useSelectedDesignStore.getState().additionListNamesFromServer]);

  const allAdditions = useMemo(() => {
    const r =
      insoleDesign?.insoles.flatMap((insole, insoleIndex) =>
        insole.additions
          .filter(addition => addition.status !== AdditionNewStatus.removed)
          .map((item, index) => ({
            item,
            insoleSide: insole.side,
            insoleIndex,
            index,
          }))
      ) || [];

    return r;
  }, [useDesignStore.getState().newAddition, useDesignStore.getState().additionRemoved, designLoaded]);

  const patient = useDesignStore((state) => state.patient);
  const patientConditionUpdated = useDesignStore((state) => state.patientConditionUpdated);
  useEffect(() => {
    if (insoleDesign == null || patient == null || !patientConditionUpdated) return;
    const conditions = patient.patientConditions;
    autoParams.current.metatarsalgia = conditions.metatarsalgia;
    autoParams.current.halluxLimitus = conditions.halluxLimitus;
    autoParams.current.sesamoiditis = conditions.sesamoiditis;

    useDesignStore.setState({ patientConditionUpdated: false });
  }, [patientConditionUpdated]);


  const handleTabClick = (tab: ViewerTabsMode) => {
    if (internalActiveTab === tab && isContentVisible) {
      setIsContentVisible(false);
      return;
    }

    setInternalActiveTab(tab);
    setIsContentVisible(true);
    useViewerStore.getState().setMode(tab);
  };

  useEffect(() => {
    const onOpenPanel = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.close || detail.tab === undefined || detail.tab === null) {
        setIsContentVisible(false);
        return;
      }
      const tab = detail.tab as ViewerTabsMode;
      setInternalActiveTab(tab);
      setIsContentVisible(true);
      useViewerStore.getState().setMode(tab);
    };
    document.addEventListener("voice:openPanel", onOpenPanel as EventListener);
    return () => document.removeEventListener("voice:openPanel", onOpenPanel as EventListener);
  }, []);

  useEffect(() => {
    const onVoiceAutomatic = async (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      try {
        await voiceAutomaticRef.current(detail);
        document.dispatchEvent(new CustomEvent("voice:automaticConditionDone", { detail: { ok: true, requestId: detail.requestId } }));
      } catch (error) {
        document.dispatchEvent(new CustomEvent("voice:automaticConditionDone", {
          detail: { ok: false, requestId: detail.requestId, error: error instanceof Error ? error.message : "Could not apply Automatic Design." },
        }));
      }
    };
    document.addEventListener("voice:automaticCondition", onVoiceAutomatic as EventListener);
    return () => document.removeEventListener("voice:automaticCondition", onVoiceAutomatic as EventListener);
  }, []);

  function hasAnyNullHardness(setting: GCodeSetting | null | undefined): boolean {
    if (!setting) return true;

    const hardnessValues: Array<Hardness | number | null> = [
      setting.foreFootHardness,
      setting.midInnerHardness,
      setting.midOuterHardness,
      setting.heelHardness,
    ];

    return hardnessValues.some(value => value === null);
  }

  const loadHardnessData = () => {
    const gcodeSettingLeft = useDesignStore.getState().gcodeSetting.find(g => g.side.toLowerCase() == 'left');
    if (gcodeSettingLeft) {
      const isLeftRegionalEnabled = !hasAnyNullHardness(gcodeSettingLeft)
      if (isLeftRegionalEnabled) {
        setLeftForefootHardness(gcodeSettingLeft.foreFootHardness);
        setLeftMidInnerHardness(gcodeSettingLeft.midInnerHardness);
        setLeftMidOuterHardness(gcodeSettingLeft.midOuterHardness);
        setLeftHeelHardness(gcodeSettingLeft.heelHardness);
      }
      else {
        gcodeSettingLeft.foreFootHardness = gcodeSettingLeft.heelHardness = gcodeSettingLeft.midInnerHardness = gcodeSettingLeft.midOuterHardness = DEFAULT_HARDNESS;
        setLeftForefootHardness(DEFAULT_HARDNESS);
        setLeftMidInnerHardness(DEFAULT_HARDNESS);
        setLeftMidOuterHardness(DEFAULT_HARDNESS);
        setLeftHeelHardness(DEFAULT_HARDNESS);
      }
    }
    const gcodeSettingRight = useDesignStore.getState().gcodeSetting.find(g => g.side.toLowerCase() == 'right');
    if (gcodeSettingRight) {
      const isRightRegionalEnabled = !hasAnyNullHardness(gcodeSettingRight)
      if (isRightRegionalEnabled) {
        setRightForefootHardness(gcodeSettingRight.foreFootHardness);
        setRightMidInnerHardness(gcodeSettingRight.midInnerHardness);
        setRightMidOuterHardness(gcodeSettingRight.midOuterHardness);
        setRightHeelHardness(gcodeSettingRight.heelHardness);
      }
      else {
        gcodeSettingRight.foreFootHardness = gcodeSettingRight.heelHardness = gcodeSettingRight.midInnerHardness = gcodeSettingRight.midOuterHardness = DEFAULT_HARDNESS;
        setRightForefootHardness(DEFAULT_HARDNESS);
        setRightMidInnerHardness(DEFAULT_HARDNESS);
        setRightMidOuterHardness(DEFAULT_HARDNESS);
        setRightHeelHardness(DEFAULT_HARDNESS);
      }
    }
  }

  useEffect(() => {
    if (useDesignStore.getState().insoleDesign === null && designLoaded === false) return;
    loadHardnessData();
    if (designLoaded) {
      // The narration design opens as a bare shell and reveals the scan data as
      // it talks about it, so this panel must not switch it all back on the
      // moment the design finishes loading. This runs after the viewer's own
      // load-time visibility, which is why guarding only there was not enough:
      // the skeleton and the lifted insole came back here.
      const bareShell = isExhibitionNarrationDesign(useSelectedDesignStore.getState().designId);
      useViewerStore.setState({ insolesVisible: true });
      useViewerStore.setState({ additionsViewMeshVisible: !bareShell });
      useViewerStore.setState({ bonesMeshVisible: !bareShell });
      useViewerStore.setState({ shoeDataMeshVisible: !bareShell });
      useViewerStore.setState({ liftedInsoleMeshVisible: !bareShell });
      useViewerStore.setState({ highHeelMeshVisible: true });
      useViewerStore.setState({ footMeshVisible: true });

      const hasBoneData = useDesignStore.getState().insoleDesign?.insoles.some(insole => !!insole.bonesGroup) ?? false;
      setHasBones(hasBoneData);
    }
  }, [designLoaded, useDesignStore.getState().gcodeSetting]);

  const newWBCT = () => {
    setHasBones(true);
  }

  useEffect(() => {
    if (useDesignStore.getState().insoleDesign?.pressureData?.ExamType == 'dynamic')
      setIsDynamicPressure(true);
    else
      setIsDynamicPressure(false);
  }, [useDesignStore.getState().insoleDesign?.pressureData]);

  const updateGcodeSettingStore = (gcodeSetting: GCodeSetting) => {
    const gcodeListClone = useDesignStore.getState().gcodeSetting.filter(g => g != gcodeSetting);
    gcodeListClone.push(gcodeSetting);
    useDesignStore.setState({ gcodeSetting: gcodeListClone });

    if (finalize) useDesignStore.setState({ GcodeReady: false });
  }

  const newGCodeSetting = (side: string) => {
    return {
      checkToRemove: null,
      designId: useSelectedDesignStore.getState().designId,
      foreFootHardness: null,
      heelHardness: null,
      id: null,
      insoleHardness: DEFAULT_HARDNESS,
      midInnerHardness: null,
      midOuterHardness: null,
      printerId: '',
      side: side
    } as GCodeSetting
  }

  const handleForefootHardnessChange = (newValue: string, foot: string) => {
    if (foot == 'left')
      setLeftForefootHardness(newValue ? newValue as Hardness : null);
    else
      setRightForefootHardness(newValue ? newValue as Hardness : null);

    const gcodeSetting = useDesignStore.getState().gcodeSetting.find(a => a.side.toLowerCase() == foot) ?? newGCodeSetting('left');
    gcodeSetting.foreFootHardness = newValue as Hardness;
    updateGcodeSettingStore(gcodeSetting);
  }

  const handleMidInnerHardnessChange = (newValue: string, foot: string) => {
    if (foot == 'left')
      setLeftMidInnerHardness(newValue ? newValue as Hardness : null);
    else
      setRightMidInnerHardness(newValue ? newValue as Hardness : null);

    const gcodeSetting = useDesignStore.getState().gcodeSetting.find(a => a.side.toLowerCase() == foot) ?? newGCodeSetting('left');
    gcodeSetting.midInnerHardness = newValue as Hardness;
    updateGcodeSettingStore(gcodeSetting);
  }

  const handleMidOuterHardnessChange = (newValue: string, foot: string) => {
    if (foot == 'left')
      setLeftMidOuterHardness(newValue ? newValue as Hardness : null);
    else
      setRightMidOuterHardness(newValue ? newValue as Hardness : null);

    const gcodeSetting = useDesignStore.getState().gcodeSetting.find(a => a.side.toLowerCase() == foot) ?? newGCodeSetting('left');
    gcodeSetting.midOuterHardness = newValue as Hardness;
    updateGcodeSettingStore(gcodeSetting);
  }

  const handleHeelHardnessChange = (newValue: string, foot: string) => {
    if (foot == 'left')
      setLeftHeelHardness(newValue ? newValue as Hardness : null);
    else
      setRightHeelHardness(newValue ? newValue as Hardness : null);

    const gcodeSetting = useDesignStore.getState().gcodeSetting.find(a => a.side.toLowerCase() == foot) ?? newGCodeSetting('left');
    gcodeSetting.heelHardness = newValue as Hardness;
    updateGcodeSettingStore(gcodeSetting);
  }

  const getProfileCurveTemplateItems = (): string[] => {
    const a = authenticatedUser?.group?.IsDarco
      ? Object.keys(ProfileCurveTemplates).filter(key =>
        ProfileCurveTemplates[key as keyof typeof ProfileCurveTemplates] === ProfileCurveTemplates.Darco)
      : Object.keys(ProfileCurveTemplates);
    return a;
  }

  const handleShoeSizeChanged = (target: HTMLSelectElement) => {
    const selectedSize = target.value as ShoeSize;
    const values = Insole.GetLenWidthByShoeSize(selectedSize);
    handleSurfaceValueChange('Length', 0, values.length, false);
    handleSurfaceValueChange('Width', 0, values.width, false);
    setSize(selectedSize);
  };

  const toggleShowVertexMarkerPosition = () => {
    useViewerStore.getState().setShowMarkerPosition(!showVertexMarkerPosition);
    closeToolbox();
  }

  const undo = async () => {
    useViewerStore.getState().setPreviousViewerMode(useViewerStore.getState().mode);
    useViewerStore.getState().setMode(ViewerTabsMode.View)
    await undoLastAutosave()
  }

  const mirrorTo = async (side: string) => {
    useViewerStore.getState().setPreviousViewerMode(useViewerStore.getState().mode);
    useViewerStore.getState().setMode(ViewerTabsMode.View)
    side === 'R' ? mirrorToRight() : mirrorToLeft();
  }

  useEffect(() => {
    setMetricChanged(true);
    const discrepancy = convertMmToViewerUnit(autoParams.current.DiscrepancyAmount);
    setLimbLengthAmount(discrepancy);

    if (useViewerStore.getState().measurementSystem == MeasurementSystem.imperial) {
      setThicknessPropertyStep(0.02);
      setPropertyStep(0.05);
    }
    else {
      setThicknessPropertyStep(0.5);
      setPropertyStep(1);
    }
  }, [useViewerStore.getState().measurementSystem]);

  const thirdQuarterUpdated = (value: boolean) => {
    if (finalize) useDesignStore.setState({ GcodeReady: false });
    useDesignStore.setState({ thirdQuarter: value });
  };


  const renderTabContent = () => {
    switch (internalActiveTab) {
      case ViewerTabsMode.Project:
        return (
          <div className="property-panel-content">
            <Box className='property-panel-title-box'>
              <h3 className='property-panel-title'>Project Settings</h3>
              <IconButton sx={{ mx: 1, mb: '-8px', display: { lg: 'none' } }} onClick={() => closeToolbox()}>
                <CloseIcon sx={{ color: 'var(--text-color)' }} />
              </IconButton>
            </Box>

            <div className="property-group">
              <details className='property-panel-detail' open>
                <summary><b> Main</b></summary>

                <div className="property-item">
                  <input
                    type="checkbox"
                    checked={insolesVisible}
                    onChange={toggleInsolesVisible}
                    className="property-item-small-start"
                  />
                  <label className="property-item-medium">{!isSandal ? "Insole:" : "Sandal:"}</label>
                  <input
                    type="color"
                    value={`#${topInsoleDefaultColor || 'ffffff'}`}
                    onChange={(e) => {
                      const hex = e.target.value.replace('#', '');
                      setTopInsoleDefaultColor(hex);
                    }}
                    disabled={!coloring ? true : false}
                    className="property-color-input"
                  />
                  <input
                    type="color"
                    value={`#${bottomInsoleDefaultColor || 'ffffff'}`}
                    onChange={(e) => {
                      const hex = e.target.value.replace('#', '');
                      setBottomInsoleDefaultColor(hex);
                    }}
                    disabled={!coloring ? true : false}
                    className="property-color-input"
                  />
                  <NumberSpinnerNoKeyboard
                    value={pressureTransparency} min={0} max={255}
                    onChange={(newValue: number) => {
                      setPressureTransparency(newValue);
                    }}
                  />
                </div>
                {(hasBones || insoleDesign?.category == DesignCategory.HighHeel) && (
                  <div className="property-item gap-10">
                    <input
                      type="checkbox"
                      checked={liftedInsoleMeshVisible}
                      onChange={toggleLiftedInsoleMeshVisible}
                      className="property-item-small-start"
                    />
                    <label className="property-item-medium">Lifted:</label>
                    <input
                      type="color"
                      value={`#${liftedInsoleDefaultColor || 'ffffff'}`}
                      onChange={(e) => {
                        const hex = e.target.value.replace('#', '');
                        setLiftedInsoleDefaultColor(hex);
                      }}
                      disabled={!coloring ? true : false}
                      className="property-color-input"
                    />
                    <NumberSpinnerNoKeyboard
                      value={liftedInsoleTransparency} min={0} max={255}
                      onChange={(newValue: number) => {
                        setLiftedInsoleTransparency(newValue);
                      }}
                    />
                  </div>
                )}
                <div className="property-item">
                  <input
                    type="checkbox"
                    checked={additionsViewMeshVisible}
                    onChange={toggleAdditionsViewMeshVisible}
                    className="property-item-small-start"
                  />
                  <label className="property-item-medium">Additions</label>
                </div>
                {isSandal && canUseDevTools && (
                  <div className="property-item">
                    <input
                      type="checkbox"
                      checked={!bandHeightClipping}
                      onChange={toggleBandHeightClipping}
                      className="property-item-small-start"
                    />
                    <label className="property-item-medium">Band Section</label>
                  </div>
                )}
                {insoleDesign?.category == DesignCategory.HighHeel && !hasBones && (<div className="property-item">
                  <input
                    type="checkbox"
                    checked={highHeelMeshVisible}
                    onChange={toggleHighHeelMeshVisible}
                    className="property-item-small-start"
                  />
                  <label className="property-item-medium">High Heel</label>
                </div>
                )}
                {hasFootMesh && (
                  <div className="property-item">
                    <input
                      type="checkbox"
                      checked={footMeshVisible}
                      onChange={toggleFootMeshVisible}
                      className="property-item-small-start"
                    />
                    <label className="property-item-medium">3D Foot</label>
                  </div>
                )}

              </details>
            </div>
            {hasBones && (
              <div className="property-group">
                <details className='property-panel-detail' open>
                  <summary><b>WBCT</b></summary>
                  <div className="property-item gap-10">
                    <input
                      type="checkbox"
                      checked={bonesMeshVisible}
                      onChange={toggleBonesMeshVisible}
                      className="property-item-small-start"
                    />
                    <label className="property-item-medium">Bones:</label>
                    <NumberSpinnerNoKeyboard
                      value={bonesTransparency} min={0} max={255}
                      onChange={(newValue: number) => {
                        setBonesTransparency(newValue);
                      }}
                    />
                  </div>
                  <div className="property-item gap-10">
                    <input
                      type="checkbox"
                      checked={shoeDataMeshVisible}
                      onChange={toggleShoeDataMeshVisible}
                      className="property-item-small-start"
                    />
                    <label className="property-item-medium">Shoes:</label>
                    <input
                      type="color"
                      value={`#${shoeDataDefaultColor || 'ffffff'}`}
                      onChange={(e) => {
                        const hex = e.target.value.replace('#', '');
                        setShoeDataDefaultColor(hex);
                      }}
                      disabled={!coloring ? true : false}
                      className="property-color-input"
                    />
                    <NumberSpinnerNoKeyboard
                      value={shoeDataTransparency} min={0} max={255}
                      onChange={(newValue: number) => {
                        setShoeDataTransparency(newValue);
                      }}
                    />
                  </div>
                </details>
              </div>
            )}
            {finalize && (
              <div className="property-group">
                <details className='property-panel-detail' open>
                  <summary><b>Internal Structures</b></summary>
                  <div className="property-item">
                    <input
                      type="checkbox"
                      checked={gcodesVisible}
                      onChange={toggleGCodesVisible}
                      className="property-item-small-start"
                    />
                    <label className="property-item-medium">GCode</label>
                  </div>
                </details>
              </div>
            )}
            {isDesktop() &&
              <div className="property-group">
                <details className='property-panel-detail' open>
                  <summary><b>Debug</b></summary>
                  <div className="property-item">
                    <input
                      type="checkbox"
                      checked={showVertexMarkerPosition}
                      onChange={toggleShowVertexMarkerPosition}
                      className="property-item-small-start"
                    />
                    <label className="property-item-medium">Vertex Marker</label>
                  </div>
                </details>
              </div>
            }
            {isDynamicPressure && !coloring && (<div className="property-group">
              <details open>
                <summary><b>Pressure</b></summary>
                <div className="property-item gap-10">
                  <label className="property-item-medium">Animate:</label>
                  <input
                    type="checkbox"
                    checked={animatePressure}
                    onChange={toggleAnimatePressure}
                    className="property-item-small"
                  />
                </div>
              </details>
            </div>
            )}
          </div>
        );
      case ViewerTabsMode.View:
        return (
          <div className="property-panel-content">
            <Box className='property-panel-title-box'>
              <h3 className='property-panel-title'>Automatic Design</h3>
              <IconButton sx={{ mx: 1, mb: '-8px', display: { lg: 'none' } }} onClick={() => closeToolbox()} >
                <CloseIcon sx={{ color: 'var(--text-color)' }} />
              </IconButton>
            </Box>
            <details className='property-panel-detail' open>
              <summary><b>General</b></summary>
              <div className="property-group">
                {authenticatedUser?.group?.IsDarco && <div className="property-item">
                  <label className='property-label'>Shoe Size</label>
                  <select value={size}
                    onChange={e => handleShoeSizeChanged(e.target)}
                  >
                    {Object.values(ShoeSize).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>}
                {!authenticatedUser?.group?.IsDarco && <div className="property-item">
                  <label className='property-label'>Insole Length</label>
                  <NumberSpinnerNoKeyboard min={convertMmToViewerUnit(150)} max={convertMmToViewerUnit(450)} step={propertyStep}
                    value={propertyValues[0]?.length}
                    onChange={(newValue: number) => {
                      handleSurfaceValueChange('Length', 0, newValue, false);
                    }}
                  />
                </div>}
                {!authenticatedUser?.group?.IsDarco && thirdQuarter && !isSandal &&
                  <div className="property-item">
                    <label className='property-label'>Partial Length</label>
                    <NumberSpinnerNoKeyboard min={convertMmToViewerUnit(150)} max={convertMmToViewerUnit(450)} step={propertyStep}
                      value={propertyValues[0]?.partialLength}
                      onChange={function () { }}
                      disabled={true}
                    />
                  </div>}
                {!authenticatedUser?.group?.IsDarco && <div className="property-item">
                  <label>Insole Width</label>
                  <NumberSpinnerNoKeyboard
                    value={propertyValues[0]?.width} min={convertMmToViewerUnit(50)} max={convertMmToViewerUnit(150)} step={propertyStep}
                    onChange={(newValue: number) => {
                      handleSurfaceValueChange('Width', 0, newValue, false);
                    }}
                  />
                </div>}
                {designCategory === DesignCategory.Insole && (<div className="property-item">
                  <label>Shoe Last</label>
                  <CustomMUISelect
                    value={autoParams.current.profileCurveRight}
                    onChange={e => handleProfileCurve(e.target as HTMLSelectElement)}
                    options={
                      getProfileCurveTemplateItems().map((value, key) => {
                        return {
                          label: ProfileCurveTemplates[value as keyof typeof ProfileCurveTemplates],
                          value: value,
                          key: key
                        };
                      })
                    }
                  />
                </div>)}
                <div className="property-item">
                  <label>Insole Template</label>
                  <CustomMUISelect
                    value={autoParams.current.insoleTemplate}
                    onChange={e => handleInsoleTemplates(e.target)}
                    options={
                      Object.keys(InsoleType).map((value, key) => {
                        return {
                          label: InsoleType[value as keyof typeof InsoleType],
                          value: value,
                          key: key
                        };
                      })
                    }
                  />
                </div>
                {!isSandal && (<div className="property-item">
                  <input type="checkbox" style={{ width: 'auto' }}
                    checked={thirdQuarter}
                    onChange={e => {
                      thirdQuarterUpdated(e.target.checked);
                    }} />
                  <label className='full-width'>3/4 Insole</label>
                </div>)}
              </div>
            </details>
            <details className='property-panel-detail' open>
              <summary ><b>Conditions</b></summary>
              <div className="property-group">
                <div className="property-item">
                  <label >Metatarsalgia</label>
                  <input type="checkbox" ref={leftMetatarsalgia} onChange={e => handleLeftMetatarsalgia(e.target.checked)} />
                  <input type="checkbox" ref={rightMetatarsalgia} onChange={e => handleRightMetatarsalgia(e.target.checked)} />
                </div>
                <div className="property-item">
                  <label >Hallux Limitus</label>
                  <input type="checkbox" ref={leftHalluxLimitus} onChange={e => handleLeftHalluxLimitus(e.target.checked)} />
                  <input type="checkbox" ref={rightHalluxLimitus} onChange={e => handleRightHalluxLimitus(e.target.checked)} />
                </div>
                <div className="property-item">
                  <label >Sesamoiditis</label>
                  <input type="checkbox" ref={leftSesamoiditis} onChange={e => handleLeftSesamoiditis(e.target.checked)} />
                  <input type="checkbox" ref={rightSesamoiditis} onChange={e => handleRightSesamoiditis(e.target.checked)} />
                </div>
                <div className="property-item">
                  <input type="checkbox" style={{ width: 'auto' }} checked={isAutomaticOffloadAdditionsEnable} onChange={e => setIsAutomaticOffloadAdditionsEnable(e.target.checked)} />
                  <label className='full-width'>Offloading Cutout</label>
                </div>
                <div className="property-item">
                  <input type="checkbox" style={{ width: 'auto' }}
                    checked={isLimbLengthEnable}
                    onChange={e => {
                      setIsLimbLengthEnable(e.target.checked);
                      handleLimbLengthDiscrepancy(e.target.checked);
                    }} />
                  <label className='full-width'>Limb Length Discrepancy</label>
                </div>
                {isLimbLengthEnable && <>
                  <div className='property-item'>
                    <label >Shorter Limb</label>
                    <CustomMUISelect
                      value={limbLengthFoot}
                      onChange={e => {
                        setLimbLengthFoot(e.target.value);
                        handleLimbLengthFootChange(e.target.value);
                      }}
                      options={[
                        { label: 'Right', value: 'Right', key: 'right' },
                        { label: 'Left', value: 'Left', key: 'left' }
                      ]}
                    />
                  </div>
                  <div className='property-item'>
                    <label >Discrepancy amount  {`(${getDefaultLengthUnit()})`}</label>
                    <NumberSpinnerNoKeyboard
                      value={limbLengthAmount}
                      min={convertMmToViewerUnit(0)}
                      max={convertMmToViewerUnit(20)}
                      step={propertyStep}
                      onChange={(newValue: number) => {
                        setLimbLengthAmount(newValue);
                      }}
                    />
                  </div>
                </>}
              </div>
            </details>
            <details className='property-panel-detail' open>
              <summary><b>Additions</b></summary>
              <div className="property-group">
                <div className="property-conditions-table">
                  <div className="property-conditions-row">
                    <span className="property-conditions-label"> Metatarsal Bar</span>
                    <div className='property-panel-conditions-inputbox'>
                      <input ref={leftMetatarsalBarInput} type="checkbox" onChange={e => handleAutomaticAdditionAdder(e.target.checked, "MB 1-5 1", 'left')} />
                      <CustomMUISelect
                        value={updateAutoAdditionHardness(1, autoParams.current.metatarSalBarLeft)}
                        onChange={(e) => handleAutoAdditionHardnessChanged(autoParams.current.metatarSalBarLeft, e.target.value)}
                        disabled={autoParams.current.metatarSalBarLeft == -1}
                        options={
                          Object.entries(Hardness).map(([key, displayValue]) => {
                            return {
                              label: displayValue,
                              value: displayValue,
                              key: key
                            };
                          })
                        }
                      />
                      <input ref={rightMetatarsalBarInput} type="checkbox" onChange={e => handleAutomaticAdditionAdder(e.target.checked, "MB 1-5 1", 'right')} />
                      <CustomMUISelect
                        value={updateAutoAdditionHardness(0, autoParams.current.metatarSalBarRight)}
                        onChange={(e) => handleAutoAdditionHardnessChanged(autoParams.current.metatarSalBarRight, e.target.value)}
                        disabled={autoParams.current.metatarSalBarRight == -1}
                        options={
                          Object.entries(Hardness).map(([key, displayValue]) => {
                            return {
                              label: displayValue,
                              value: displayValue,
                              key: key
                            };
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="property-conditions-row">
                    <span className="property-conditions-label"> Metatarsal Pad</span>
                    <div className='property-panel-conditions-inputbox'>
                      <input ref={leftMetatarsalPadInput} type="checkbox" onChange={e => handleAutomaticAdditionAdder(e.target.checked, "MP Pad 1", 'left')} />
                      <CustomMUISelect
                        value={updateAutoAdditionHardness(1, autoParams.current.metatarSalPadLeft)}
                        onChange={(e) => handleAutoAdditionHardnessChanged(autoParams.current.metatarSalPadLeft, e.target.value)}
                        disabled={autoParams.current.metatarSalPadLeft == -1}
                        options={
                          Object.entries(Hardness).map(([key, displayValue]) => {
                            return {
                              label: displayValue,
                              value: displayValue,
                              key: key
                            };
                          })
                        }
                      />
                      <input ref={rightMetatarsalPadInput} type="checkbox" onChange={e => handleAutomaticAdditionAdder(e.target.checked, "MP Pad 1", 'right')} />
                      <CustomMUISelect
                        value={updateAutoAdditionHardness(0, autoParams.current.metatarSalPadRight)}
                        onChange={(e) => handleAutoAdditionHardnessChanged(autoParams.current.metatarSalPadRight, e.target.value)}
                        disabled={autoParams.current.metatarSalPadRight == -1}
                        options={
                          Object.entries(Hardness).map(([key, displayValue]) => {
                            return {
                              label: displayValue,
                              value: displayValue,
                              key: key
                            };
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="property-conditions-row">
                    <span className="property-conditions-label">Morton's Extention</span>
                    <div className='property-panel-conditions-inputbox'>
                      <input ref={leftMortonsExtensionInput} type="checkbox" onChange={e => handleAutomaticAdditionAdder(e.target.checked, "Morton's Extention", 'left')} />
                      <CustomMUISelect
                        value={updateAutoAdditionHardness(1, autoParams.current.mortonsExtensionLeft)}
                        onChange={(e) => handleAutoAdditionHardnessChanged(autoParams.current.mortonsExtensionLeft, e.target.value)}
                        disabled={autoParams.current.mortonsExtensionLeft == -1}
                        options={
                          Object.entries(Hardness).map(([key, displayValue]) => {
                            return {
                              label: displayValue,
                              value: displayValue,
                              key: key
                            };
                          })
                        }
                      />
                      <input ref={rightMortonsExtensionInput} type="checkbox" onChange={e => handleAutomaticAdditionAdder(e.target.checked, "Morton's Extention", 'right')} />
                      <CustomMUISelect
                        value={updateAutoAdditionHardness(0, autoParams.current.mortonsExtensionRight)}
                        onChange={(e) => handleAutoAdditionHardnessChanged(autoParams.current.mortonsExtensionRight, e.target.value)}
                        disabled={autoParams.current.mortonsExtensionRight == -1}
                        options={
                          Object.entries(Hardness).map(([key, displayValue]) => {
                            return {
                              label: displayValue,
                              value: displayValue,
                              key: key
                            };
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="property-conditions-row">
                    <span className="property-conditions-label">Dancers Pad</span>
                    <div className='property-panel-conditions-inputbox'>
                      <input ref={leftDancerPadInput} type="checkbox" onChange={e => handleAutomaticAdditionAdder(e.target.checked, 'Pad_2', 'left')} />
                      <CustomMUISelect
                        value={updateAutoAdditionHardness(1, autoParams.current.dancersPadLeft)}
                        onChange={(e) => handleAutoAdditionHardnessChanged(autoParams.current.dancersPadLeft, e.target.value)}
                        disabled={autoParams.current.dancersPadLeft == -1}
                        options={
                          Object.entries(Hardness).map(([key, displayValue]) => {
                            return {
                              label: displayValue,
                              value: displayValue,
                              key: key
                            };
                          })
                        }
                      />
                      <input ref={rightDancerPadInput} type="checkbox" onChange={e => handleAutomaticAdditionAdder(e.target.checked, 'Pad_2', 'right')} />
                      <CustomMUISelect
                        value={updateAutoAdditionHardness(0, autoParams.current.dancersPadRight)}
                        onChange={(e) => handleAutoAdditionHardnessChanged(autoParams.current.dancersPadRight, e.target.value)}
                        disabled={autoParams.current.dancersPadRight == -1}
                        options={
                          Object.entries(Hardness).map(([key, displayValue]) => {
                            return {
                              label: displayValue,
                              value: displayValue,
                              key: key
                            };
                          })
                        }
                      />
                    </div>
                  </div>
                  {isAutomaticOffloadAdditionsEnable && <>
                    <div className='property-item' style={{ marginBottom: 0 }}>
                      <label>Cutout Met 1</label>
                      <div className='property-panel-conditions-inputbox'>
                        <input ref={leftCutoutMet1} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'left', 1)} />
                        <div className='property-placeholder-box'>
                        </div>
                        <input ref={rightCutoutMet1} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'right', 1)} />
                        <div className='property-placeholder-box'>
                        </div>
                      </div>
                    </div>
                    <div className='property-item' style={{ marginBottom: 0 }}>
                      <label>Cutout Met 2</label>
                      <div className='property-panel-conditions-inputbox'>
                        <input ref={leftCutoutMet2} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'left', 2)} />
                        <div className='property-placeholder-box'>
                        </div>
                        <input ref={rightCutoutMet2} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'right', 2)} />
                        <div className='property-placeholder-box'>
                        </div>
                      </div>
                    </div>
                    <div className='property-item' style={{ marginBottom: 0 }}>
                      <label>Cutout Met 3</label>
                      <div className='property-panel-conditions-inputbox'>
                        <input ref={leftCutoutMet3} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'left', 3)} />
                        <div className='property-placeholder-box'>
                        </div>
                        <input ref={rightCutoutMet3} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'right', 3)} />
                        <div className='property-placeholder-box'>
                        </div>
                      </div>
                    </div>
                    <div className='property-item' style={{ marginBottom: 0 }}>
                      <label>Cutout Met 4</label>
                      <div className='property-panel-conditions-inputbox'>
                        <input ref={leftCutoutMet4} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'left', 4)} />
                        <div className='property-placeholder-box'>
                        </div>
                        <input ref={rightCutoutMet4} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'right', 4)} />
                        <div className='property-placeholder-box'>
                        </div>
                      </div>
                    </div>
                    <div className='property-item' style={{ marginBottom: 0 }}>
                      <label>Cutout Met 5</label>
                      <div className='property-panel-conditions-inputbox'>
                        <input ref={leftCutoutMet5} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'left', 5)} />
                        <div className='property-placeholder-box'>
                        </div>
                        <input ref={rightCutoutMet5} type='checkbox' onChange={e => handleAutomaticDesignCutoutAddition(e.target.checked, 'right', 5)} />
                        <div className='property-placeholder-box'>
                        </div>
                      </div>
                    </div>
                  </>}
                </div>
              </div>
            </details>
            <details className='property-panel-detail' open={true}>
              <summary><b>Details</b></summary>
              <div className="property-group">
                <div className="property-item property-item-stacked">
                  <label className="property-conditions-label">Heel Depth</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelDepth} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Depth', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelDepth} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Depth', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                {!isSandal && (<div className="property-item property-item-stacked">
                  <label className="property-conditions-label">Heel Fill</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelFill} min={0} max={100}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Fill', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelFill} min={0} max={100}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Fill', 0, newValue);
                      }}
                    />
                  </div>
                </div>)}
                <div className="property-item property-item-stacked">
                  <label className="property-conditions-label">Heel Height</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelHeight} min={convertMmToViewerUnit(0)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Height', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelHeight} min={convertMmToViewerUnit(0)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Height', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label className="property-conditions-label">Arch Height</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.archMedialHeight} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Medial Height', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.archMedialHeight} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Medial Height', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                {!isSandal && (<div className="property-item property-item-stacked">
                  <label className="property-conditions-label">Arch Fill</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.archFill} min={0} max={100} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Fill', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.archFill} min={0} max={100} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Fill', 0, newValue);
                      }}
                    />
                  </div>
                </div>)}
                <div className="property-item property-item-stacked">
                  <label className="property-conditions-label">Medial Wedge<sup>°</sup></label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelMedialWedge} min={0} max={10} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Medial Wedge', 1, { value: newValue, min: 0, max: 10 });
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelMedialWedge} min={0} max={10} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Medial Wedge', 0, { value: newValue, min: 0, max: 10 });
                      }}
                    />
                  </div>
                </div>
              </div>
            </details>
            <div className='toolbar-container'>
              <div className="toolbar-item">
                <button className="panel-toolbar-button"
                  onClick={() => {
                    undo();
                  }}>
                  <ReplyIcon />
                </button>
                <span className="toolbar-label">Undo</span>
              </div>
              <div className="toolbar-item">
                <button className="panel-toolbar-button"
                  onClick={() => mirrorTo('R')}>
                  <SwitchRightIcon />
                </button>
                <span className="toolbar-label">Mirror Left</span>
              </div>
              <div className="toolbar-item">
                <button className="panel-toolbar-button"
                  onClick={() => mirrorTo('L')}>
                  <SwitchLeftIcon />
                </button>
                <span className="toolbar-label">Mirror Right</span>
              </div>
            </div>
          </div>
        );
      case ViewerTabsMode.Edit:
        return (
          <div className="property-panel-content">
            <Box className='property-panel-title-box'>
              <h3 className='property-panel-title'>Edit Surface</h3>
              <IconButton sx={{ mx: 1, mb: '-8px', display: { lg: 'none' } }} onClick={() => closeToolbox()}>
                <CloseIcon sx={{ color: 'var(--text-color)' }} />
              </IconButton>
            </Box>
            <details className='property-panel-detail' open>
              <summary><b>Main</b></summary>
              <div className="property-group">
                {designCategory === DesignCategory.Insole && (<div className="property-item property-item-stacked">
                  <label>Shoe Last</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <CustomMUISelect
                      value={autoParams.current.profileCurveLeft}
                      onChange={e => handleSurfaceValueChange('Profile Curve', 1, e.target)}
                      options={
                        getProfileCurveTemplateItems().map((value, key) => {
                          return {
                            label: ProfileCurveTemplates[value as keyof typeof ProfileCurveTemplates],
                            value: value,
                            key: key
                          };
                        })
                      }
                    />
                    <CustomMUISelect
                      value={autoParams.current.profileCurveRight}
                      onChange={e => handleSurfaceValueChange('Profile Curve', 0, e.target)}
                      options={
                        getProfileCurveTemplateItems().map((value, key) => {
                          return {
                            label: ProfileCurveTemplates[value as keyof typeof ProfileCurveTemplates],
                            value: value,
                            key: key
                          };
                        })
                      }
                    />
                  </div>
                </div>)}
                <div className="property-item property-item-stacked">
                  <label>Length</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.length} min={convertMmToViewerUnit(150)} max={convertMmToViewerUnit(450)} step={propertyStep}
                      onChange={(newValue: number) => {
                        setSize(Insole.GetShoeSize(newValue));
                        handleSurfaceValueChange('Length', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.length} min={convertMmToViewerUnit(150)} max={convertMmToViewerUnit(450)} step={propertyStep}
                      onChange={(newValue: number) => {
                        setSize(Insole.GetShoeSize(newValue));
                        handleSurfaceValueChange('Length', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                {thirdQuarter && !isSandal && <div className="property-item property-item-stacked">
                  <label>Partial Length</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.partialLength} min={convertMmToViewerUnit(150)} max={convertMmToViewerUnit(450)} step={propertyStep}
                      onChange={function () { }}
                      disabled={true}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.partialLength} min={convertMmToViewerUnit(150)} max={convertMmToViewerUnit(450)} step={propertyStep}
                      onChange={function () { }}
                      disabled={true}
                    />
                  </div>
                </div>}
                <div className="property-item property-item-stacked">
                  <label>Width</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.width} min={convertMmToViewerUnit(50)} max={convertMmToViewerUnit(150)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Width', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.width} min={convertMmToViewerUnit(50)} max={convertMmToViewerUnit(150)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Width', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Thickness</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.thickness} min={convertMmToViewerUnit(2.5)} max={convertMmToViewerUnit(10)} step={thicknessPropertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Thickness', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.thickness} min={convertMmToViewerUnit(2.5)} max={convertMmToViewerUnit(10)} step={thicknessPropertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Thickness', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                {isSandal && <div className="property-item property-item-stacked">
                  <label>Outsole Thickness</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.outsoleThickness} min={convertMmToViewerUnit(outsoleThicknessJoint.min)} max={convertMmToViewerUnit(outsoleThicknessJoint.max)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Outsole Thickness', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.outsoleThickness} min={convertMmToViewerUnit(outsoleThicknessJoint.min)} max={convertMmToViewerUnit(outsoleThicknessJoint.max)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Outsole Thickness', 0, newValue);
                      }}
                    />
                  </div>
                </div>}
                {isSandal && <div className="property-item property-item-stacked">
                  <label>Band Height</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.bandHeight} min={BandJoints.min * convertMmToViewerUnit(propertyValues[1].length)} max={BandJoints.max * convertMmToViewerUnit(propertyValues[1].length)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Band Height', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.bandHeight} min={BandJoints.min * convertMmToViewerUnit(propertyValues[0].length)} max={BandJoints.max * convertMmToViewerUnit(propertyValues[0].length)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Band Height', 0, newValue);
                      }}
                    />
                  </div>
                </div>}
                {isSandal && <div className="property-item property-item-stacked">
                  <label>Instep Area</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      disabled={true}
                      value={propertyValues[1]?.instepArea}
                      onChange={(newValue: number) => {
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      disabled={true}
                      value={propertyValues[0]?.instepArea}
                      onChange={(newValue: number) => {
                      }}
                    />
                  </div>
                </div>}
                {!isSandal && (<div className="property-item property-item-stacked">
                  <label>Heel Fill%</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelFill} min={0} max={100} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Fill', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelFill} min={0} max={100} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Fill', 0, newValue);
                      }}
                    />
                  </div>
                </div>)}
                {!isSandal && (<div className="property-item property-item-stacked">
                  <label>Arch Fill%</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.archFill} min={0} max={100} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Fill', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.archFill} min={0} max={100} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Fill', 0, newValue);
                      }}
                    />
                  </div>
                </div>)}
                {insoleDesign?.category === DesignCategory.HighHeel && !hasBones && (<div className="property-item property-item-stacked">
                  <label>High-Heel Elevation</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.highHeelHeight}
                      min={convertMmToViewerUnit(HighHeelHeight.min)}
                      max={convertMmToViewerUnit(HighHeelHeight.max)}
                      step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('High Heel Height', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.highHeelHeight}
                      min={convertMmToViewerUnit(HighHeelHeight.min)}
                      max={convertMmToViewerUnit(HighHeelHeight.max)}
                      step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('High Heel Height', 0, newValue);
                      }}
                    />
                  </div>
                </div>)}
              </div>
            </details>
            <details className='property-panel-detail' open>
              <summary><b>Heel</b></summary>
              <div className="property-group">
                <div className="property-item property-item-stacked">
                  <label>Height</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelHeight} min={convertMmToViewerUnit(0)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Height', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelHeight} min={convertMmToViewerUnit(0)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Height', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Depth</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelDepth} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Depth', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelDepth} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Depth', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Wedge Type</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <CustomMUISelect
                      value={propertyValues[1]?.heelWedgeType}
                      onChange={e => handleWedgeTypeChange('Heel Wedge Type', 1, e.target)}
                      options={[
                        { label: 'Medial', value: 'Medial', key: 'Medial' },
                        { label: 'Lateral', value: 'Lateral', key: 'Lateral' },
                        { label: 'Free', value: 'Free', key: 'Free' }
                      ]}
                    />
                    <CustomMUISelect
                      value={propertyValues[0]?.heelWedgeType}
                      onChange={e => handleWedgeTypeChange('Heel Wedge Type', 0, e.target)}
                      options={[
                        { label: 'Medial', value: 'Medial', key: 'Medial' },
                        { label: 'Lateral', value: 'Lateral', key: 'Lateral' },
                        { label: 'Free', value: 'Free', key: 'Free' }
                      ]}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Medial Wedge<sup>°</sup></label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelMedialWedge} min={0} max={10} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Medial Wedge', 1, newValue);
                      }}
                      disabled={!isLeftHeelMedialWedgeEditEnabled}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelMedialWedge} min={0} max={10} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Medial Wedge', 0, newValue);
                      }}
                      disabled={!isRightHeelMedialWedgeEditEnabled}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Lateral Wedge<sup>{String.fromCharCode(176)}</sup></label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.heelLateralWedge} min={0} max={10} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Lateral Wedge', 1, newValue);
                      }}
                      disabled={!isLeftHeelLateralWedgeEditEnabled}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.heelLateralWedge} min={0} max={10} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Heel Lateral Wedge', 0, newValue);
                      }}
                      disabled={!isRightHeelLateralWedgeEditEnabled}
                    />
                  </div>
                </div>
              </div>
            </details>
            <details className='property-panel-detail' open>
              <summary><b>Arch</b></summary>
              <div className="property-group">
                <div className="property-item property-item-stacked">
                  <label>Medial Height</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.archMedialHeight} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Medial Height', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.archMedialHeight} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Medial Height', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Lateral Height</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.archLateralHeight} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Lateral Height', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.archLateralHeight} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Lateral Height', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Center Height</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.archCenterHeight} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Center Height', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.archCenterHeight} min={convertMmToViewerUnit(1)} max={convertMmToViewerUnit(40)} step={propertyStep}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Center Height', 0, newValue);
                      }}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Sagital Position</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.archSagitalPosition} min={50} max={95} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Sagital Position', 1, newValue);
                      }}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.archSagitalPosition} min={50} max={95} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Arch Sagital Position', 0, newValue);
                      }}
                    />
                  </div>
                </div>
              </div>
            </details>
            <details className='property-panel-detail' open>
              <summary><b>ForeFoot</b></summary>
              <div className="property-group">
                <div className="property-item property-item-stacked">
                  <label>Wedge Type</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <CustomMUISelect
                      value={propertyValues[1]?.forefootWedgeType}
                      onChange={e => handleWedgeTypeChange('Forefoot Wedge Type', 1, e.target)}
                      options={[
                        { label: 'Medial', value: 'Medial', key: 'Medial' },
                        { label: 'Lateral', value: 'Lateral', key: 'Lateral' },
                        { label: 'Free', value: 'Free', key: 'Free' }
                      ]}
                    />
                    <CustomMUISelect
                      value={propertyValues[0]?.forefootWedgeType}
                      onChange={e => handleWedgeTypeChange('Forefoot Wedge Type', 0, e.target)}
                      options={[
                        { label: 'Medial', value: 'Medial', key: 'Medial' },
                        { label: 'Lateral', value: 'Lateral', key: 'Lateral' },
                        { label: 'Free', value: 'Free', key: 'Free' }
                      ]}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Medial Wedge<sup>{String.fromCharCode(176)}</sup></label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.forefootMedialWedge} min={0} max={10} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Forefoot Medial Wedge', 1, newValue);
                      }}
                      disabled={!isLeftForeFootMedialWedgeEditEnabled}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.forefootMedialWedge} min={0} max={10} step={1}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Forefoot Medial Wedge', 0, newValue);
                      }}
                      disabled={!isRightForeFootMedialWedgeEditEnabled}
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label>Lateral Wedge<sup>{String.fromCharCode(176)}</sup></label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[1]?.forefootLateralWedge}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Forefoot Lateral Wedge', 1, { value: newValue, min: 0, max: 10 });
                      }}
                      disabled={!isLeftForeFootLateralWedgeEditEnabled}
                    />
                    <NumberSpinnerNoKeyboard
                      value={propertyValues[0]?.forefootLateralWedge}
                      onChange={(newValue: number) => {
                        handleSurfaceValueChange('Forefoot Lateral Wedge', 0, { value: newValue, min: 0, max: 10 });
                      }}
                      disabled={!isRightForeFootLateralWedgeEditEnabled}
                    />
                  </div>
                </div>
              </div>
            </details>
            <div className='toolbar-container'>
              <div className="toolbar-item">
                <button className="panel-toolbar-button" onClick={undo}>
                  <ReplyIcon />
                </button>
                <span className="toolbar-label">Undo</span>
              </div>
              <div className="toolbar-item">
                <button className="panel-toolbar-button" onClick={() => mirrorTo('R')}>
                  <SwitchRightIcon />
                </button>
                <span className="toolbar-label">Mirror Left</span>
              </div>
              <div className="toolbar-item">
                <button className="panel-toolbar-button" onClick={() => mirrorTo('L')}>
                  <SwitchLeftIcon />
                </button>
                <span className="toolbar-label">Mirror Right</span>
              </div>
              {!isSandal && (<div className="toolbar-item">
                <button className={`panel-toolbar-button ${advancedIsActive ? "active" : ""}`} onClick={toggleAdvance}  >
                  <DeveloperBoardOffIcon />
                </button>
                <span className="toolbar-label">Advanced</span>
              </div>)}
            </div>
          </div>
        );
      case ViewerTabsMode.Additions:
        return (
          <div className="property-panel-content">
            <Box className='property-panel-title-box'>
              <h3 className='property-panel-title'>Edit Hardness & Additions</h3>
              <IconButton sx={{ mx: 1, mb: '-8px', display: { lg: 'none' } }} onClick={() => closeToolbox()}>
                <CloseIcon sx={{ color: 'var(--text-color)' }} />
              </IconButton>
            </Box>
            <details className='property-panel-detail' open>
              <summary><b>Hardness:</b></summary>
              <div className="property-group">
                <div className="property-item property-item-stacked">
                  <label className="property-label">1. Forefoot</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <CustomMUISelect
                      value={leftForefootHardness || DEFAULT_HARDNESS}
                      onChange={(e) => handleForefootHardnessChange(e.target.value, 'left')}
                      options={Object.keys(Hardness).map((value, key) => {
                        return {
                          label: Hardness[value as keyof typeof Hardness],
                          value: value,
                          key: key
                        }
                      })
                      }
                    />
                    <CustomMUISelect
                      value={rightForefootHardness || DEFAULT_HARDNESS}
                      onChange={(e) => handleForefootHardnessChange(e.target.value, 'right')}
                      options={Object.keys(Hardness).map((value, key) => {
                        return {
                          label: Hardness[value as keyof typeof Hardness],
                          value: value,
                          key: key
                        }
                      })
                      }
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label className="property-label">2. Midfoot Inner</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <CustomMUISelect
                      value={leftMidInnerHardness || DEFAULT_HARDNESS}
                      onChange={(e) => handleMidInnerHardnessChange(e.target.value, 'left')}
                      options={Object.keys(Hardness).map((value, key) => {
                        return {
                          label: Hardness[value as keyof typeof Hardness],
                          value: value,
                          key: key
                        }
                      })
                      }
                    />
                    <CustomMUISelect
                      value={rightMidInnerHardness || DEFAULT_HARDNESS}
                      onChange={(e) => handleMidInnerHardnessChange(e.target.value, 'right')}
                      options={Object.keys(Hardness).map((value, key) => {
                        return {
                          label: Hardness[value as keyof typeof Hardness],
                          value: value,
                          key: key
                        }
                      })
                      }
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label className="property-label">3. Midfoot Outer</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <CustomMUISelect
                      value={leftMidOuterHardness || DEFAULT_HARDNESS}
                      onChange={(e) => handleMidOuterHardnessChange(e.target.value, 'left')}
                      options={Object.keys(Hardness).map((value, key) => {
                        return {
                          label: Hardness[value as keyof typeof Hardness],
                          value: value,
                          key: key
                        }
                      })
                      }
                    />
                    <CustomMUISelect
                      value={rightMidOuterHardness || DEFAULT_HARDNESS}
                      onChange={(e) => handleMidOuterHardnessChange(e.target.value, 'right')}
                      options={Object.keys(Hardness).map((value, key) => {
                        return {
                          label: Hardness[value as keyof typeof Hardness],
                          value: value,
                          key: key
                        }
                      })
                      }
                    />
                  </div>
                </div>
                <div className="property-item property-item-stacked">
                  <label className="property-label">4. Heel</label>
                  <div className='property-panel-conditions-inputbox small-gap'>
                    <CustomMUISelect
                      value={leftHeelHardness || DEFAULT_HARDNESS}
                      onChange={(e) => handleHeelHardnessChange(e.target.value, 'left')}
                      options={Object.keys(Hardness).map((value, key) => {
                        return {
                          label: Hardness[value as keyof typeof Hardness],
                          value: value,
                          key: key
                        }
                      })
                      }
                    />
                    <CustomMUISelect
                      value={rightHeelHardness || DEFAULT_HARDNESS}
                      onChange={(e) => handleHeelHardnessChange(e.target.value, 'right')}
                      options={Object.keys(Hardness).map((value, key) => {
                        return {
                          label: Hardness[value as keyof typeof Hardness],
                          value: value,
                          key: key
                        }
                      })
                      }
                    />
                  </div>
                </div>
              </div>
            </details>
            <details className='property-panel-detail' open>
              <summary><b>Add New Additions:</b> {isDesktop() && '(Double-click to Add)'}</summary>
              <div className="property-group">
                <div className="property-item" style={{ display: 'block' }} >
                  <div className="image-selection">
                    {additionImagesFilename.map(({ group, fileNames }) => (
                      <div key={group} className="image-group">
                        <div className="image-group-header">{group}</div>
                        <div className="image-grid-group">
                          {chunkArray(fileNames, 3).map((row, rowIdx) => {
                            // Pad the row to always have 3 items
                            const paddedRow = [...row];
                            while (paddedRow.length < 3) paddedRow.push(null);

                            return (
                              <div className="image-row" key={rowIdx}>
                                {paddedRow.map((fileName, idx) =>
                                  fileName ? (
                                    <div
                                      key={fileName}
                                      className={`image-item ${selectedAdditionImage === fileName ? 'selected' : ''}`}
                                      onDoubleClick={() => handleNewAdditionDoubleClicked(fileName)}
                                      onClick={() => handleNewAdditionClicked(fileName)}
                                    >
                                      <img
                                        src={`/assets/Additions/${group}/${fileName}.png`}
                                        alt={fileName}
                                      />
                                      <span className="image-name">{fileName}</span>
                                    </div>
                                  ) : (
                                    <div key={`empty-${idx}`} className="image-item empty"></div>
                                  )
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </details>
            <details className='property-panel-detail' open>
              <summary><b>Current Additions:</b></summary>
              <div className="property-group">
                <div className="property-item">
                  {(insoleDesign !== null && (insoleDesign.insoles[0].additions.length > 0 || insoleDesign.insoles[1].additions.length > 0)) ? (
                    <div>
                      <table className="additions-table" id='additions'>
                        <thead>
                          <tr>
                            <th>Foot</th>
                            <th>Name</th>
                            <th>Height</th>
                            <th>Hardness</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {allAdditions.map(({ item, insoleSide, insoleIndex, index }) => (
                            <tr key={`${insoleIndex}-${index}`}
                              className={
                                selectedAdditionIndex === index &&
                                  insoleIndex === selectedFoot
                                  ? "selected-addition"
                                  : ""
                              }
                              onClick={() => {
                                useViewerStore.setState({
                                  selectedAdditionFromViewer: {
                                    foot: insoleSide === 'right' ? 0 : 1, additionIndex: index
                                  }
                                });
                              }}>
                              <td >
                                {`${insoleSide}`}
                              </td>
                              <td >
                                {`${item.name}`}
                              </td>
                              <td >
                                <NumberSpinnerNoKeyboard
                                  min={convertMmToViewerUnit(0)} max={convertMmToViewerUnit(10)} step={propertyStep}
                                  value={
                                    convertMmToViewerUnit(
                                      additionHeights[item.foot + index] ?
                                        Number(additionHeights[item.foot + index]) :
                                        item.height.value
                                    )}
                                  onChange={(newValue: number) => {
                                    handleHeightAdditionChanged(item, index, newValue.toString());
                                  }}
                                // isOneDigit={true}
                                />
                              </td>
                              <td >
                                <CustomMUISelect
                                  value={item.hardness}
                                  onChange={(e) => {
                                    handleAdditionHardnessChange(item, index, e.target.value);
                                  }}
                                  options={
                                    Object.entries(Hardness).map(([key, displayValue]) => {
                                      return {
                                        label: (key === Hardness.Medium && authenticatedUser?.group?.IsRondom) ? 'Medium (SL40)'
                                          : (key === Hardness.Soft && authenticatedUser?.group?.IsRondom) ? 'Soft (X2)'
                                            : (key === Hardness.ExtraSoft && authenticatedUser?.group?.IsRondom) ? "Extra Soft (PPT)"
                                              : displayValue,
                                        value: displayValue,
                                        key: key
                                      };
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveAdditionClicked(insoleIndex, index)}
                                  aria-label="delete addition"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>

                      </table>
                    </div>
                  ) : (
                    <p className="no-additions">No additions yet</p>
                  )}
                </div>
              </div>
            </details >
            <div className='toolbar-container no-pad'>
              <div className="toolbar-item">
                <button className="panel-toolbar-button" onClick={undo}>
                  <ReplyIcon />
                </button>
                <span className="toolbar-label">Undo</span>
              </div>
              <div className="toolbar-item">
                <button className="panel-toolbar-button" onClick={() => mirrorTo('R')}>
                  <SwitchRightIcon />
                </button>
                <span className="toolbar-label">Mirror Left</span>
              </div>
              <div className="toolbar-item">
                <button className="panel-toolbar-button" onClick={() => mirrorTo('L')}>
                  <SwitchLeftIcon />
                </button>
                <span className="toolbar-label">Mirror Right</span>
              </div>
            </div>
          </div >
        );
      case ViewerTabsMode.Engraving:
        return (<Engraving onClose={closeToolbox} />);
      default:
        return null;
    }
  };

  const onFloatingButtonClick = () => {
    setVisible(!isVisible);
  }

  return (
    <>
      <Box className="property-panel-wrapper">
        {/* <button className='property-panel-floating-button' onClick={onFloatingButtonClick}>
          <MenuIcon style={{color: 'var(--toolbar-button-border)'}}/>
        </button> */}
        <Slide in={isVisible} direction='up'>
          <div className="property-panel">

            <button
              className={`property-panel-tab ${internalActiveTab === ViewerTabsMode.View ? 'active' : ''}`}
              onClick={() => handleTabClick(ViewerTabsMode.View)}
              data-tour-id="tab-view"
              title="Automatic Design"
              disabled={!isPropertyPanelTabsEnabled}
            >
              <StarIcon />
            </button>
            <button
              className={`property-panel-tab ${internalActiveTab === ViewerTabsMode.Edit ? 'active' : ''}`}
              onClick={() => handleTabClick(ViewerTabsMode.Edit)}
              data-tour-id="tab-edit"
              title="Edit Surface"
              disabled={!isPropertyPanelTabsEnabled}
            >
              <EditSurfaceIcon />
            </button>
            <button
              className={`property-panel-tab ${internalActiveTab === ViewerTabsMode.Additions ? 'active' : ''}`}
              onClick={() => handleTabClick(ViewerTabsMode.Additions)}
              data-tour-id="tab-additions"
              title="Edit Additions & Hardness"
              disabled={!isPropertyPanelTabsEnabled}
            >
              <ControlCameraIcon />
            </button>
            <button
              className={`property-panel-tab ${internalActiveTab === ViewerTabsMode.Engraving ? 'active' : ''}`}
              onClick={() => handleTabClick(ViewerTabsMode.Engraving)}
              data-tour-id="tab-engraving"
              title="Edit Engraving"
              disabled={!isPropertyPanelTabsEnabled}
            >
              <EngravingIcon />
            </button>
            <button
              className={`property-panel-tab ${internalActiveTab === ViewerTabsMode.Project ? 'active' : ''}`}
              onClick={() => handleTabClick(ViewerTabsMode.Project)}
              data-tour-id="tab-project"
              disabled={!isPropertyPanelTabsEnabled}
            >
              <ChecklistIcon />
            </button>
          </div>
        </Slide>
      </Box>
      <div
        ref={contentRef}
        className={`property-panel-content-container ${isContentVisible ? 'visible' : 'hidden'}`}
        style={{
          width: (
            internalActiveTab === ViewerTabsMode.View ||
            internalActiveTab === ViewerTabsMode.Edit ||
            internalActiveTab === ViewerTabsMode.Additions
          ) ? '400px' :
            internalActiveTab === ViewerTabsMode.Engraving ? '273px' :
              '288px'
        }}
      >
        {renderTabContent()}
      </div>
    </>
  );
};

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

export default PropertyPanel;
